import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronIcon,
  CycleIcon,
  IntakeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@/components/catalog/icons';
import {
  MasterFormModal,
  type MasterKind,
  type MasterRecordSeed,
} from '@/components/catalog-admin/MasterFormModal';
import { BillingRegionDefaultsCard } from '@/components/catalog-admin/BillingRegionDefaultsCard';
import { CatalogStatusBadge } from '@/components/catalog-admin/CatalogMasterBits';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ContactGroupsCard } from '@/components/catalog-admin/ContactGroupsCard';
import { CompanyStageStatusesCard } from '@/components/catalog-admin/CompanyStageStatusesCard';
import { CompanyMapperPanel } from '@/components/company-mapper/CompanyMapperPanel';
import { AccountLeadImportModal } from '@/components/catalog/AccountLeadImportModal';
import { StageSwatch } from '@/components/StageSwatch';
import { CycleFormModal } from '@/components/cycles/CycleFormModal';
import { IntakeFormModal } from '@/components/intakes/IntakeFormModal';
import { GroupTargetsCard } from '@/components/targets/GroupTargetsCard';
import { TargetPeriodsCard } from '@/components/targets/TargetPeriodsCard';
import { ApiError, api } from '@/lib/api';
import type { ReferenceMasterKind } from '@/lib/api';
import { cx } from '@/lib/cx';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { formatDateOnly, orDash, pluralise } from '@/lib/format';
import { denialReason, type Ability, type ModuleSlug } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { usePageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import type {
  BillingRegionDefault,
  CampaignInclusion,
  City,
  CompanyBillingRegion,
  Contact,
  ContactListParams,
  Country,
  Currency,
  Cycle,
  CycleListParams,
  DealStage,
  DealType,
  Intake,
  IntakeListParams,
  LeadSource,
  Paginated,
  PickerListParams,
  ReferenceListParams,
  ReferenceRecord,
} from '@/lib/types';
import {
  Badge,
  Button,
  Checkbox,
  ColumnManager,
  ConfirmDialog,
  EmptyState,
  type FrozenSide,
  Modal,
  type ModalSize,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelBody,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';

/*
 * The reference-data index for SEED EMERGE: a card per managed list, and full
 * create / edit / deactivate behind each card.
 *
 * THE SHAPE OF THIS SCREEN IS THE POINT. Every other surface in the app reads
 * these lists through a picker, which offers only the active rows. This one
 * documents what the CRM is *configured with*, which is a different question
 * and has a different answer — a retired value is exactly what makes an older
 * record's stored value make sense, so it belongs here even though it belongs
 * nowhere else.
 */

/*
 * `perPage: 200` is the ceiling on the four picklists, and the default of 50 is
 * NOT the whole list. One page covers every master today, but `meta.totalPages`
 * is still checked and a truncated list says so rather than quietly passing
 * page one off as the whole set.
 *
 * `isActive` is deliberately ABSENT, and must stay absent. Unfiltered, these
 * endpoints return active and retired rows alike. `use-reference-options.ts`
 * pins `isActive: true` for exactly the opposite reason — a picker offering a
 * retired id is offering a guaranteed 422 — and the difference between the two
 * queries is the difference between a picker and a catalogue. Filtering this
 * screen to the active rows would hide the values this screen exists to explain,
 * and would also hide the Reactivate control from the only place it is offered.
 */
const REFERENCE_QUERY: ReferenceListParams = { page: 1, perPage: 200 };

/*
 * Countries and currencies have their own, higher ceiling: 250, chosen by the
 * backend to clear today's 195 countries and 164 currencies with headroom.
 * Asking for 200 would fit now and quietly start paging later, so ask for the
 * real maximum. 251 is a 422.
 */
const WIDE_QUERY: ReferenceListParams = { page: 1, perPage: 250 };

/*
 * Cities have their OWN ceiling again: 1..200, default 50. Not the countries
 * and currencies 250 that sits directly above, and not the entities' 100 —
 * asking for 250 here is a 422 naming `perPage`, and omitting it pages silently
 * at 50. Three different ceilings on one screen is why each is a named constant
 * with the number written down rather than a literal reused by eye.
 *
 * As with `REFERENCE_QUERY`, `isActive` stays absent for the same reason. The
 * city form's own region picker does the opposite and pins `isActive: true`,
 * because a retired region is a guaranteed 422 at write time.
 */
const CITY_QUERY: ReferenceListParams = { page: 1, perPage: 200 };

/*
 * Cycles and intakes are ENTITY endpoints, not masters: their `perPage` ceiling
 * is 100, not 200 or 250, and asking for more is a 422. So this is the largest
 * page either will serve, and `TruncationNotice` — plus the link to the full
 * page in the modal footer — covers the day one of them outgrows it.
 *
 * They also accept no `search`, no sort and no `isActive`, because none of the
 * three exists on these endpoints. Unknown query params are stripped silently,
 * so a control for any of them would look like it worked.
 */
const ENTITY_QUERY = { page: 1, perPage: 100 } as const;

/*
 * The three DEAL PICKERS — stages, types and lead sources — are a different
 * backend module from the reference masters, and their ceiling is a THIRD
 * number again: 1..100, default 20. Asking for 200 is a 422 naming `perPage`,
 * and omitting it pages silently at 20 — which would fit today's handful of
 * rows and start truncating the moment a pipeline outgrows it.
 *
 * The filter is spelled `active` here, not `isActive`, and is deliberately
 * absent for the same reason it's absent from the masters' query above.
 *
 * One constant for all three rather than one each: they share the endpoint
 * family and the number, and two constants holding the same number is how one
 * of them drifts.
 */
const PICKER_QUERY: PickerListParams = { page: 1, perPage: 100 };

/*
 * Contacts is indexed here, not managed here: the card carries a count and a
 * link, and `/contacts` remains the page that does the work. So this asks for
 * the smallest page the API will serve and reads `meta.total` off it — the rows
 * themselves are never rendered, and pulling 20 or 100 of them to display one
 * number would be paying for data this screen throws away.
 */
const COUNT_QUERY: ContactListParams = { page: 1, perPage: 1 };

const SKELETON_ROWS = 5;

/** A stable empty list for the stage-statuses card while stages load. */
const EMPTY_STAGES: readonly ReferenceRecord[] = [];

// --- icons -------------------------------------------------------------
//
// Inlined rather than pulled from a package: this app has no icon dependency.
// Everything is 24x24, `currentColor` and `aria-hidden`, so an icon never
// contributes to an accessible name.

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function CompassIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <circle cx="12" cy="12" r="10" />
      <path d="m16.24 7.76-2.12 6.36-6.36 2.12 2.12-6.36z" />
    </svg>
  );
}

function BookmarkIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function BriefcaseIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}

function MegaphoneIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="m3 11 18-5v12L3 14z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}

function CoinsIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <circle cx="9" cy="9" r="6" />
      <path d="M15.5 4.2a6 6 0 0 1 0 15.6" />
      <path d="M9 6.5v5" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

/** Campaign inclusions: a ticked list, which is what one looks like on a quote. */
function ChecklistIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="m3 6 1.75 1.75L8 4.5" />
      <path d="m3 15 1.75 1.75L8 13.5" />
      <path d="M12 6.5h9" />
      <path d="M12 15.5h9" />
    </svg>
  );
}

/*
 * The same two-people silhouette the sidebar used for its Contacts row, at the
 * 20px size this page's cards use. Carried over deliberately: the row moved
 * here, so the mark someone learned to look for moves with it.
 */
/** Company mapper: two linked nodes, platform company to SEED EMERGE company. */
function MapperIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <rect x="2" y="3" width="8" height="7" rx="1.5" />
      <rect x="14" y="14" width="8" height="7" rx="1.5" />
      <path d="M6 10v3a2 2 0 0 0 2 2h6" />
    </svg>
  );
}

/** Account lead history: a clock with a back-arrow, periods over time. */
function LeadHistoryIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l4 2" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="3.5" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

/**
 * Deal stages: a funnel, which is what a pipeline is drawn as everywhere else in
 * this app. Deliberately NOT a palette or a swatch — the colour is one column on
 * the row, and an icon leading with it would say this list is about colours.
 */
function FunnelIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M3 5h18l-7 8v6l-4 2v-8z" />
    </svg>
  );
}

/**
 * Company stages: a run of steps, left to right, with the current one filled.
 *
 * Deliberately NOT the funnel above. A deal funnel narrows — most of what
 * enters it never reaches the bottom — whereas Lead → Prospect → Client →
 * Past Client is a state a single account moves along, and can move back
 * along. Two lists that read as the same shape would invite exactly the
 * conflation the two tables exist to keep apart.
 */
function StepsIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <circle cx="5" cy="12" r="2.5" />
      <circle cx="12" cy="12" r="2.5" />
      <circle cx="19" cy="12" r="2.5" />
      <path d="M7.5 12h2" />
      <path d="M14.5 12h2" />
    </svg>
  );
}

/**
 * Company sources: two routes merging into one account.
 *
 * Deliberately NOT the steps above and NOT the funnel. A stage is a position a
 * single account occupies and moves along; a SOURCE is where that account
 * arrived from, which is fixed the moment it arrives — so the mark shows
 * several inbound routes converging on one node rather than a run of states.
 * The two cards sit next to each other on this page, and a reader who cannot
 * tell the marks apart will not read the titles either.
 */
function InflowIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <circle cx="18" cy="12" r="3" />
      <path d="M4 7h3a5 5 0 0 1 5 5" />
      <path d="M4 17h3a5 5 0 0 0 5-5" />
      <path d="M12.5 9.5 15 12l-2.5 2.5" />
    </svg>
  );
}

/**
 * Company types: two differently-shaped marks, side by side over their own
 * rules — one account is a university, the next is a partner agency, and the
 * list is the set of kinds rather than a route or a progression.
 *
 * Deliberately NOT the inflow above, NOT the steps, and NOT the tag already
 * taken by deal types. Every one of those says something about MOVEMENT or
 * about labelling one record; a type is a classification the counterparty
 * already has before we meet it. Two shapes that are obviously not each other
 * is the whole content of the mark — a single shape repeated would read as a
 * list of one kind of thing, which is the opposite of what this master holds.
 */
function KindsIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <rect x="3.5" y="4" width="7" height="7" rx="1.5" />
      <circle cx="17" cy="7.5" r="3.5" />
      <path d="M4 16.5h6" />
      <path d="M14 16.5h6" />
    </svg>
  );
}

/** Quote stages: a document, because a quote stage is a state of a document. */
function QuoteDocIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6" />
      <path d="M9 17h4" />
    </svg>
  );
}

/** Invoice statuses: a receipt, the one document on this page that carries money. */
function ReceiptIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2z" />
      <path d="M9 8h6" />
      <path d="M9 12h6" />
    </svg>
  );
}

/** Deal types: a tag, because a type labels a deal rather than moving it. */
function TagIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M3 12.5V4a1 1 0 0 1 1-1h8.5a1 1 0 0 1 .7.3l7.5 7.5a1 1 0 0 1 0 1.4l-8.5 8.5a1 1 0 0 1-1.4 0L3.3 13.2a1 1 0 0 1-.3-.7z" />
      <circle cx="7.5" cy="7.5" r="1.25" />
    </svg>
  );
}

/** Lead sources: a signpost — where somebody came FROM, not where they are. */
function SignpostIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M12 3v18" />
      <path d="M12 6h6l2 2.5-2 2.5h-6" />
      <path d="M12 14H6l-2-2.5" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <path d="M10 12h4" />
    </svg>
  );
}

function RestoreIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </svg>
  );
}

// --- loading ------------------------------------------------------------

interface ListState<T> {
  result: Paginated<T> | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * `P` is the endpoint's own query type. The masters share `ReferenceListParams`;
 * cycles and intakes have their own, narrower ones — they accept `page` and
 * `perPage` and nothing else — and widening them to the masters' type here would
 * let a `search` or an `isActive` be passed to an endpoint that silently drops it.
 */
type Loader<T, P = ReferenceListParams> = (params: P, signal: AbortSignal) => Promise<Paginated<T>>;

/*
 * Every dataset loads on its own. They are independent requests against
 * independent endpoints, so one failing must leave the others rendered — a
 * single combined promise would blank the whole page because ads plans were
 * briefly unavailable.
 *
 * Written as arrow wrappers, and declared at module scope, so `this` stays
 * bound to `api` and the identities stay stable across renders.
 */
const loadRegions: Loader<ReferenceRecord> = (params, signal) => api.listRegions(params, signal);
const loadBookingStatuses: Loader<ReferenceRecord> = (params, signal) =>
  api.listBookingStatuses(params, signal);
const loadTravelPackages: Loader<ReferenceRecord> = (params, signal) =>
  api.listTravelPackages(params, signal);
const loadAdsPlans: Loader<ReferenceRecord> = (params, signal) => api.listAdsPlans(params, signal);
/*
 * The other masters served by the same parameterised module — the same row
 * shape, the same write schema, the same 200 ceiling. `company-stages` is the
 * pipeline every company sits on and `company-sources` is where each one came
 * from; the other two back the quote and invoice lists.
 */
const loadCompanyStages: Loader<ReferenceRecord> = (params, signal) =>
  api.listCompanyStages(params, signal);
const loadCompanySources: Loader<ReferenceRecord> = (params, signal) =>
  api.listCompanySources(params, signal);
const loadCompanyTypes: Loader<ReferenceRecord> = (params, signal) =>
  api.listCompanyTypes(params, signal);
const loadQuoteStages: Loader<ReferenceRecord> = (params, signal) =>
  api.listQuoteStages(params, signal);
const loadInvoiceStatuses: Loader<ReferenceRecord> = (params, signal) =>
  api.listInvoiceStatuses(params, signal);
const loadDealLostReasons: Loader<ReferenceRecord> = (params, signal) =>
  api.listDealLostReasonRecords(params, signal);
const loadContactTitles: Loader<ReferenceRecord> = (params, signal) =>
  api.listContactTitles(params, signal);
const loadPriceTiers: Loader<ReferenceRecord> = (params, signal) =>
  api.listPriceTierRecords(params, signal);
/*
 * The two remaining deal pickers. A different module, a different query type,
 * and a different `perPage` ceiling — hence `PickerListParams` rather than
 * `ReferenceListParams`, which would let this page send an `isActive` these
 * endpoints silently strip.
 *
 * `/lead-sources` is mounted TOP-LEVEL and shared: deals reference it today and
 * contacts are expected to adopt it next, so nothing here files it under deals.
 */
const loadDealTypes: Loader<DealType, PickerListParams> = (params, signal) =>
  api.listDealTypes(params, signal);
const loadLeadSources: Loader<LeadSource, PickerListParams> = (params, signal) =>
  api.listLeadSources(params, signal);
const loadCurrencies: Loader<Currency> = (params, signal) => api.listCurrencies(params, signal);
const loadCountries: Loader<Country> = (params, signal) => api.listCountries(params, signal);
const loadCities: Loader<City> = (params, signal) => api.listCities(params, signal);
const loadCampaignInclusions: Loader<CampaignInclusion> = (params, signal) =>
  api.listCampaignInclusions(params, signal);
const loadDealStages: Loader<DealStage, PickerListParams> = (params, signal) =>
  api.listDealStages(params, signal);
const loadCycles: Loader<Cycle, CycleListParams> = (params, signal) => api.listCycles(params, signal);
const loadIntakes: Loader<Intake, IntakeListParams> = (params, signal) =>
  api.listIntakes(params, signal);
const loadContacts: Loader<Contact, ContactListParams> = (params, signal) =>
  api.listContacts(params, signal);

function useMasterList<T, P = ReferenceListParams>(
  load: Loader<T, P>,
  noun: string,
  query: P,
): ListState<T> {
  const [result, setResult] = useState<Paginated<T> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /** Bumped by `reload()` after any write, which re-runs the effect. */
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    /*
     * Deferred a tick rather than fired in the effect body: the fetch flips the
     * loading flag synchronously, and doing that inside the effect cascades a
     * render. Same reason as `CyclesPage`.
     */
    const timer = setTimeout(() => {
      setLoading(true);
      void load(query, controller.signal)
        .then((data) => {
          if (controller.signal.aborted) return;
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted) return;
          setError(caught instanceof Error ? caught.message : `Failed to load ${noun}`);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `query` is a module-level constant on every call site, so its identity is
    // stable and this does not refetch on render.
  }, [load, noun, query, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { result, error, loading, reload };
}

/**
 * A master this page joins against LOCALLY, carried as three states rather than
 * as a bare `Map`.
 *
 * ⚠️ **AN EMPTY INDEX IS NOT "STILL LOADING", and that is the whole reason this
 * type exists.** A `Map` on its own cannot tell the two apart: a master whose
 * request is still out and a master that legitimately returned zero rows are
 * both an index with nothing in it, so every lookup misses in both — and a cell
 * that renders a miss as an em dash then says "this record has no value" about a
 * record whose value has simply not been fetched.
 *
 * That was a live defect, and it was found by disagreeing browser runs rather
 * than by anything failing: at 1440 Afghanistan's Currency cell read `—` and at
 * 1280 the same country read `AFN`, on the same build against the same data. The
 * first run merely read the table before the currency fetch landed. Nothing
 * errored, nothing logged, and `—` is exactly what the column shows for a
 * country that genuinely has no currency.
 *
 * So `status` comes from the FETCH's own state — `result` and `error` on
 * `ListState` — and **never from `index.size`**.
 */
type MasterJoin<T> =
  /** No result and no error yet: the request is still out. Nothing is known. */
  | { status: 'pending' }
  /** The request came back a failure. There is nothing to resolve against. */
  | { status: 'unreadable' }
  /** The master arrived. An empty `index` here is a real, answered "no rows". */
  | { status: 'ready'; index: Map<number, T> };

/**
 * Build a `MasterJoin` out of a `useMasterList` result.
 *
 * ⚠️ **A kept `result` wins over a set `error`, deliberately.** `useMasterList`
 * holds the previous payload across a `reload()`, so a failed REFRESH leaves the
 * data that is on screen resolved rather than replacing already-correct values
 * with skeletons. `unreadable` is reserved for having nothing at all.
 */
function masterJoin<T>(
  result: Paginated<T> | null,
  error: string | null,
  key: (row: T) => number,
): MasterJoin<T> {
  if (result !== null) {
    const index = new Map<number, T>();
    for (const row of result.data) index.set(key(row), row);
    return { status: 'ready', index };
  }
  return error !== null ? { status: 'unreadable' } : { status: 'pending' };
}

/**
 * `/billing-region-defaults` indexed by slug, for the Countries list's Billing
 * region column. Slug → the server's display name, and nothing else.
 *
 * ⚠️ **It does NOT go through `useMasterList`, and it cannot.** That hook is
 * typed to `Paginated<T>`, and this endpoint is **UNPAGINATED** — `{data: [...]}`
 * with no `meta` at all — so a pager written by habit reads `meta.total` off
 * `undefined`. Three rows for ever, no POST and no DELETE; there is nothing to
 * page.
 *
 * ⚠️ **A FAILED READ RETURNS AN EMPTY MAP AND SAYS SO NOWHERE, on purpose.**
 * This is a LABEL lookup, not the data: every country still renders its billing
 * region, as the raw slug, because `countryRow` falls back to it. Surfacing an
 * error block here would report "we could not fetch three display names" as a
 * failure of the countries list, which loaded fine. The degradation is visible
 * in the cells themselves — `united_kingdom` rather than "United Kingdom" is
 * unmistakable and is exactly the string somebody needs to go and look.
 *
 * ⛔ **Do NOT hardcode the three names as a fallback.** They are admin-editable
 * columns on `f_billing_region_defaults`; a literal map here would be a second
 * copy of wording the server owns, correct on the day it was written and
 * silently wrong after the first rename — with nothing failing.
 *
 * `BillingRegionDefaultsCard` runs its own copy of this fetch. That is a
 * duplicated REQUEST, not duplicated wording, and the two cannot disagree
 * because both render whatever the server sent.
 */
function useBillingRegionNames(): Map<string, string> {
  const [rows, setRows] = useState<BillingRegionDefault[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    void api
      .listBillingRegionDefaults(controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        setRows(envelope.data);
      })
      .catch(() => {
        // Deliberately silent — see the note above. The slug still renders.
      });
    return () => controller.abort();
  }, []);

  return useMemo(() => {
    const index = new Map<string, string>();
    for (const row of rows) index.set(row.slug, row.name);
    return index;
  }, [rows]);
}

// --- the master registry ------------------------------------------------

type Shape =
  | 'reference'
  | 'currency'
  | 'country'
  | 'city'
  | 'inclusion'
  | 'dealStage'
  /** Deal types and lead sources — see `MasterFormModal`'s `Shape` for why. */
  | 'picker';

interface MasterMeta {
  kind: MasterKind;
  shape: Shape;
  title: string;
  /**
   * One line, on the card. Optional, and omitted on every master whose list
   * speaks for itself: helper text is not added here. The few that remain are
   * REFUSALS rather than hints — they name a row that a rename or a delete
   * silently breaks — so they stay whatever they look like.
   */
  blurb?: string;
  /** Plural, lower case, used mid-sentence. */
  plural: string;
  /** Singular, lower case, used mid-sentence. */
  singular: string;
  icon: ReactNode;
  /**
   * False for countries only. The backend has confirmed the country delete has
   * NO in-use guard — no 409 — and has not yet verified whether anything holds
   * a foreign key to `f_countries`. Until they do, a country delete is
   * immediate, permanent and unprotected, so the control does not exist and
   * `api.ts` deliberately has no `deleteCountry` to call.
   */
  deletable: boolean;
}

/*
 * Which catalogue module governs each master — owner's split of
 * `reference-data`, 2026-10-02. A master not named in the split
 * (`bookingStatuses`, `travelPackages`, `adsPlans`, `companyTypes`) stays on
 * `reference-data`. An exhaustive `Record`, so a new master must be classified
 * before this file compiles.
 */
const MASTER_MODULE: Record<MasterKind, ModuleSlug> = {
  regions: 'reference-geography',
  currencies: 'reference-geography',
  countries: 'reference-geography',
  cities: 'reference-geography',
  dealStages: 'reference-sales',
  dealTypes: 'reference-sales',
  leadSources: 'reference-sales',
  quoteStages: 'reference-sales',
  invoiceStatuses: 'reference-sales',
  companyStages: 'reference-sales',
  companySources: 'reference-sales',
  campaignInclusions: 'reference-sales',
  dealLostReasons: 'reference-sales',
  contactTitles: 'reference-sales',
  priceTiers: 'reference-sales',
  bookingStatuses: 'reference-data',
  travelPackages: 'reference-data',
  adsPlans: 'reference-data',
  companyTypes: 'reference-data',
};

const MASTERS: MasterMeta[] = [
  {
    kind: 'regions',
    shape: 'reference',
    title: 'Regions of focus',
    plural: 'regions of focus',
    singular: 'region of focus',
    icon: <CompassIcon />,
    deletable: true,
  },
  {
    kind: 'bookingStatuses',
    shape: 'reference',
    title: 'Booking statuses',
    plural: 'booking statuses',
    singular: 'booking status',
    icon: <BookmarkIcon />,
    deletable: true,
  },
  {
    kind: 'travelPackages',
    shape: 'reference',
    title: 'Travel packages',
    plural: 'travel packages',
    singular: 'travel package',
    icon: <BriefcaseIcon />,
    deletable: true,
  },
  {
    kind: 'adsPlans',
    shape: 'reference',
    title: 'Ads plans',
    plural: 'ads plans',
    singular: 'ads plan',
    icon: <MegaphoneIcon />,
    deletable: true,
  },
  {
    kind: 'currencies',
    shape: 'currency',
    title: 'Currencies',
    plural: 'currencies',
    singular: 'currency',
    icon: <CoinsIcon />,
    deletable: true,
  },
  {
    kind: 'countries',
    shape: 'country',
    title: 'Countries',
    plural: 'countries',
    singular: 'country',
    icon: <GlobeIcon />,
    deletable: false,
  },
  {
    kind: 'cities',
    shape: 'city',
    title: 'Cities',
    plural: 'cities',
    singular: 'city',
    icon: <MapPinIcon />,
    /*
     * Unlike countries, a city delete IS guarded: `f_project_cities`,
     * `f_quote_products` and `f_invoice_products` all reference it ON DELETE
     * RESTRICT, and all three are counted before the DELETE, so a city in use
     * comes back as a 409 naming the counts rather than removing itself from
     * under a quote. That guard is what makes offering the control safe.
     */
    deletable: true,
  },
  {
    kind: 'campaignInclusions',
    shape: 'inclusion',
    title: 'Campaign inclusions',
    plural: 'campaign inclusions',
    singular: 'campaign inclusion',
    icon: <ChecklistIcon />,
    /*
     * Guarded like a city rather than open like a country:
     * `f_quote_product_inclusions` and `f_invoice_product_inclusions` both
     * reference it ON DELETE RESTRICT and are counted before the DELETE, so an
     * inclusion any line still names comes back as a 409 naming the counts.
     */
    deletable: true,
  },
  {
    kind: 'dealStages',
    shape: 'dealStage',
    title: 'Deal stages',
    plural: 'deal stages',
    singular: 'deal stage',
    icon: <FunnelIcon />,
    /*
     * Guarded: `DELETE /deal-stages/{id}` is a 409 naming the deal count while
     * any deal still points at the stage, and nothing is reassigned. That guard
     * is what makes offering the control safe — and with 79 of the 79 deals in
     * production sitting on three of these six rows, it is the guard that will
     * actually fire.
     */
    deletable: true,
  },
  {
    /*
     * Lead / Prospect / Client / Past Client is a reference master, not four
     * values in code, so a fifth stage is a row someone adds — not a migration
     * and a deploy.
     */
    kind: 'companyStages',
    shape: 'reference',
    title: 'Company stages',
    plural: 'company stages',
    singular: 'company stage',
    icon: <StepsIcon />,
    /*
     * Guarded: `DELETE /company-stages/{id}` is a 409 naming the company count
     * while any company holds the stage. `d_companies.stage_id` is actually `ON
     * DELETE SET NULL` — the one RESTRICT exception in the schema — but the
     * count runs before the DELETE either way, so the 409 always fires first.
     */
    deletable: true,
  },
  {
    /*
     * Where an account CAME FROM — Organic / Conferences / Referral / Website /
     * Social Media, seeded — and a master for the same reason the stages are
     * one: a sixth source is a row somebody adds, not a migration and a deploy.
     *
     * ⚠️ NOT a second spelling of the stage above it. A stage is where an
     * account IS and moves as the account progresses; a source is where it
     * ARRIVED FROM and does not change afterwards. They sit adjacently here on
     * purpose and must stay tellable apart in title, noun and mark.
     */
    kind: 'companySources',
    shape: 'reference',
    title: 'Company sources',
    plural: 'company sources',
    singular: 'company source',
    icon: <InflowIcon />,
    /*
     * Offered like every other reference master. The delete guard is the
     * server's and is the same shape as the stages' — a 409 naming the company
     * count while any company holds the source.
     *
     * ⚠️ **A zero count here is ORDINARY rather than evidence the row is
     * spare.** `d_companies.source_id` is nullable and blank is the usual state
     * on that column, so a seeded source nothing points at yet deletes cleanly
     * and irreversibly. Retiring with `isActive: false` is still the real
     * "remove": it keeps the row resolving on whatever does eventually hold it.
     */
    deletable: true,
  },
  {
    /*
     * What KIND of organisation an account is — Institution / Partner, seeded —
     * and a master for the same reason the two above are: a third kind is a row
     * somebody adds, not a migration and a deploy.
     *
     * ⚠️ The THIRD column on `d_companies` edited on this page, and it is
     * neither of its neighbours. A STAGE is where the account is and moves as
     * the account progresses; a SOURCE is where it arrived from and is fixed at
     * arrival; a TYPE is what the organisation already is, independently of us.
     * A university that came in through a conference and sits at Prospect
     * carries one row from each of the three lists, and nothing about any one of
     * them implies the others.
     */
    kind: 'companyTypes',
    shape: 'reference',
    title: 'Company types',
    plural: 'company types',
    singular: 'company type',
    icon: <KindsIcon />,
    /*
     * Offered like every other reference master. The delete guard is the
     * server's and is the same shape as the stages' and the sources' — a 409
     * naming the company count while any company holds the type.
     *
     * ⚠️ **A zero count here is ORDINARY rather than evidence the row is
     * spare.** `d_companies.company_type_id` is nullable and blank is the usual
     * state on that column, so a seeded type nothing points at yet deletes
     * cleanly and irreversibly. Retiring with `isActive: false` is still the
     * real "remove": it keeps the row resolving on whatever does eventually hold
     * it.
     */
    deletable: true,
  },
  {
    kind: 'quoteStages',
    shape: 'reference',
    title: 'Quote stages',
    plural: 'quote stages',
    singular: 'quote stage',
    icon: <QuoteDocIcon />,
    /*
     * Guarded like the rest: a 409 naming the quote count while any quote
     * points at the stage.
     *
     * ⚠️ There is a SECOND hazard here that no 409 covers, which is why the
     * blurb says so on the card rather than only in the modal. `POST
     * /quotes/{id}/convert` resolves `Accepted` by name and DEGRADES SILENTLY
     * when no such row exists — the quote simply keeps the stage it had, with
     * no error anywhere. Renaming or retiring that row is therefore a change
     * with an invisible consequence, and the same is true of `Draft` on the
     * invoice statuses below.
     */
    deletable: true,
  },
  {
    kind: 'invoiceStatuses',
    shape: 'reference',
    title: 'Invoice statuses',
    plural: 'invoice statuses',
    singular: 'invoice status',
    icon: <ReceiptIcon />,
    deletable: true,
  },
  {
    /*
     * Why a deal was lost (migration 285), asked when a deal moves to an
     * `isLost` stage. Guarded like the rest: `d_deals.lost_reason_id` is a
     * RESTRICT FK, so a reason in use is a 409 and is retired instead.
     */
    kind: 'dealLostReasons',
    shape: 'reference',
    title: 'Deal lost reasons',
    plural: 'deal lost reasons',
    singular: 'deal lost reason',
    icon: <TagIcon />,
    deletable: true,
  },
  {
    /*
     * Suggestions for a contact's `title` (owner 2026-10-18). The contact
     * column stays free text, so nothing references these rows by id.
     */
    kind: 'contactTitles',
    shape: 'reference',
    title: 'Contact titles',
    plural: 'contact titles',
    singular: 'contact title',
    icon: <TagIcon />,
    deletable: true,
  },
  {
    /*
     * Project price tiers (migration 326, owner 2026-10-27): Standard /
     * Exclusive / Early bird. Exactly one carries `isDefault`; the server moves
     * it on a write and refuses to clear, retire or delete the holder (409
     * `REFERENCE_DEFAULT_REQUIRED`). Lines and project prices reference tiers
     * RESTRICT, so a tier in use is a 409 and is retired instead.
     */
    kind: 'priceTiers',
    shape: 'reference',
    title: 'Price tiers',
    plural: 'price tiers',
    singular: 'price tier',
    icon: <CoinsIcon />,
    deletable: true,
  },
  {
    kind: 'dealTypes',
    shape: 'picker',
    title: 'Deal types',
    plural: 'deal types',
    singular: 'deal type',
    icon: <TagIcon />,
    /*
     * Guarded: a 409 naming the deal count while any deal holds the type. Note
     * the message wording differs from the masters' — `Deal type {id} is still
     * used by {n} deal(s) — set isActive false to retire it instead` — which is
     * exactly why the handler switches on the STATUS and renders the server's
     * own message rather than matching on text.
     */
    deletable: true,
  },
  {
    /*
     * ⚠️ SHARED REFERENCE DATA, NOT A DEALS SETTING. `/lead-sources` is mounted
     * top-level, deals reference it today and contacts are expected to adopt it
     * next — the delete-conflict message is already built from a per-consumer
     * count with a slot for the second consumer. So the blurb describes it as
     * "where an enquiry came from" rather than as a deal field, and the 409 is
     * rendered verbatim: it will start naming contacts with no API version
     * bump, and a message composed here from a deal count would then be wrong.
     */
    kind: 'leadSources',
    shape: 'picker',
    title: 'Lead sources',
    plural: 'lead sources',
    singular: 'lead source',
    icon: <SignpostIcon />,
    deletable: true,
  },
];

// --- cycles and intakes -------------------------------------------------

/*
 * The other two managed lists, and they are NOT masters — so almost none of the
 * behaviour above transfers:
 *
 * - There is no `isActive` column on either table. They cannot be retired, so
 *   the Deactivate/Reactivate control does not exist here and the catalogue's
 *   usual "deactivate instead of delete" framing would be a promise the API
 *   cannot keep. Delete is the only removal.
 * - `startDate` / `endDate` are plain 'YYYY-MM-DD' strings, never Dates.
 * - The audit column is `modifiedAt`, not `updatedAt` — and on an intake it is
 *   NOT NULL and equals `createdAt` on a brand-new row, so `modifiedBy === null`
 *   is the only reliable "never edited" signal on either resource.
 * - `createdBy` / `modifiedBy` are opaque provenance strings. They are never
 *   resolved against /users and never typed by anyone; this app does not send
 *   them at all.
 *
 * The forms are `CycleFormModal` and `IntakeFormModal`, unchanged and shared
 * with /cycles and /intakes — this screen is another way in, not a second
 * implementation.
 */
type PeriodKind = 'cycles' | 'intakes';

interface PeriodMeta {
  kind: PeriodKind;
  /** Same string as the permission module, but they are separate concepts. */
  module: ModuleSlug;
  title: string;
  blurb?: string;
  plural: string;
  singular: string;
  icon: ReactNode;
  /** The full page, still the home of paging and column filters. */
  path: string;
  pageLabel: string;
}

const PERIODS: PeriodMeta[] = [
  {
    kind: 'cycles',
    module: 'cycles',
    title: 'Cycles',
    plural: 'cycles',
    singular: 'cycle',
    icon: <CycleIcon />,
    path: '/cycles',
    pageLabel: 'Open the full cycles page',
  },
  {
    kind: 'intakes',
    module: 'intakes',
    title: 'Intakes',
    plural: 'intakes',
    singular: 'intake',
    icon: <IntakeIcon />,
    path: '/intakes',
    pageLabel: 'Open the full intakes page',
  },
];

/**
 * One cycle or intake row, flattened so a single table renders both. `seed` is
 * the record itself, handed straight to its form — as with the masters, the
 * loaded row IS the form's starting point.
 */
interface PeriodRow {
  id: number;
  name: string;
  /** Plain 'YYYY-MM-DD'. Compared and sorted as strings, never as Dates. */
  startDate: string;
  endDate: string;
  createdAt: string;
  createdBy: string;
  modifiedAt: string | null;
  /** `null` is the reliable "never edited" signal on both resources. */
  modifiedBy: string | null;
  /** Intakes only: the parent cycle, joined client-side. */
  cycleLabel: string | null;
  seed: Cycle | Intake;
}

function cycleRow(record: Cycle): PeriodRow {
  return {
    id: record.id,
    name: record.cycleName,
    startDate: record.startDate,
    endDate: record.endDate,
    createdAt: record.createdAt,
    createdBy: record.createdBy,
    modifiedAt: record.modifiedAt,
    modifiedBy: record.modifiedBy,
    cycleLabel: null,
    seed: record,
  };
}

/**
 * @param cycles The cycle list indexed by id.
 *
 * The intake read does NOT embed its cycle — only `cycleId` — so the name is a
 * local join against the list this page has already loaded for its own card.
 * When the id is not on that page the raw id is shown rather than a blank cell:
 * "Cycle #14" is a fact the reader can act on, an em dash is not.
 */
function intakeRow(record: Intake, cycles: Map<number, Cycle>): PeriodRow {
  return {
    id: record.id,
    name: record.intakeName,
    startDate: record.startDate,
    endDate: record.endDate,
    createdAt: record.createdAt,
    createdBy: record.createdBy,
    modifiedAt: record.modifiedAt,
    modifiedBy: record.modifiedBy,
    cycleLabel: cycles.get(record.cycleId)?.cycleName ?? `Cycle #${record.cycleId}`,
    seed: record,
  };
}

/*
 * `in` narrows the `Cycle | Intake` union without a cast, so the wrong record
 * can never reach the wrong form.
 */
function cycleSeed(row: PeriodRow | null): Cycle | undefined {
  if (!row) return undefined;
  return 'cycleName' in row.seed ? row.seed : undefined;
}

function intakeSeed(row: PeriodRow | null): Intake | undefined {
  if (!row) return undefined;
  return 'intakeName' in row.seed ? row.seed : undefined;
}

// --- normalised rows ----------------------------------------------------

/**
 * One cell whose value is RESOLVED AGAINST A SEPARATELY-FETCHED MASTER, as four
 * distinct states rather than as `string | null`.
 *
 * `string | null` could not express this column. `null` had to stand for both
 * "this record has no value" and "the master it resolves against has not
 * arrived", and `orDash` rendered the two as the same em dash — so a cell that
 * had answered nothing looked exactly like a cell that had answered "none".
 *
 * The rule it now keeps is one this codebase draws everywhere else: `0` linked
 * inclusions versus `null` unread ones, a mailer that is *configured* versus one
 * that is *working*, a group target's `actual: null` versus a zero. **A value
 * that has not been read must never render as a value that was read and found
 * absent.**
 */
type JoinedCell =
  /** Resolved. `text` is the master's own label. */
  | { kind: 'value'; text: string }
  /** The record genuinely holds no value. The em dash, and it stays the em dash. */
  | { kind: 'absent' }
  /** The master has not arrived, so nothing is known and nothing is claimed. */
  | { kind: 'pending' }
  /**
   * The master could not be read, or came back without this id in it. Carries
   * the RAW IDENTIFIER we were handed — see `JoinedValue` for why that is
   * rendered rather than blanked.
   */
  | { kind: 'unresolved'; text: string };

/** Shared, because "no value" carries nothing per-row. */
const CELL_ABSENT: JoinedCell = { kind: 'absent' };
/** Shared, for the same reason. */
const CELL_PENDING: JoinedCell = { kind: 'pending' };

/**
 * One row of any of the six masters, flattened so a single table can render
 * them. `seed` is the record itself, handed straight to the form — there is no
 * get-by-id on these endpoints, so the loaded row IS the form's starting point.
 */
interface Row {
  id: number;
  name: string;
  /** Currency or country code. */
  code: string | null;
  dialling: string | null;
  popular: boolean;
  sortOrder: number | null;
  isActive: boolean;
  updatedAt: string;
  /**
   * Cities only, and optional so the five older mappers need no edit. `null` is
   * a REAL state on both — "no country recorded" and "unclassified" — rather
   * than a value that failed to load, so both render as an em dash and neither
   * is ever filled in from the other.
   */
  countryLabel?: string | null;
  regionLabel?: string | null;
  /**
   * Countries only, and optional for the same reason as the two above — the
   * other thirteen mappers have no such value.
   *
   * TWO FIELDS FOR ONE FACT, AND THE SPLIT IS THE POINT. `billingRegion` is the
   * server's SLUG and is the only thing anything may match on — the filter keys
   * on it, and `BillingRegionDefault.name` is admin-editable free text that this
   * codebase has been bitten by three times when read as a flag.
   * `billingRegionLabel` is display only.
   *
   * ⚠️ **`billingRegionLabel` is NEVER null and never an em dash.** Measured
   * 2026-08-31: all 195 countries carry a billing region and the column is a NOT
   * NULL ENUM, so there is no "unclassified" state to render. Where the label
   * cannot be resolved the raw slug is shown instead — see `countryRow`. A blank
   * cell here would report a FAILED LOOKUP as MISSING DATA, and those are
   * different facts.
   */
  billingRegion?: CompanyBillingRegion;
  billingRegionLabel?: string;
  /**
   * Countries only, and the ONE cell on this page resolved against a master
   * fetched by a different request — `/currencies`, indexed by id.
   *
   * ⚠️ **It is a `JoinedCell` and not a `string | null` for exactly that
   * reason.** The other thirteen mappers have no currency at all and so set
   * nothing here, which is the same shape the four fields above use; `undefined`
   * and `absent` render identically, and neither is ever reached outside the
   * country table.
   */
  currencyLabel?: JoinedCell;
  /**
   * Campaign inclusions only, and optional for the same reason as the two
   * above. It is the two stored unit forms shown side by side, NEVER one
   * derived from the other — see `inclusionRow`.
   */
  unitLabel?: ReactNode;
  /**
   * Deal stages only, and optional for the same reason as the three above.
   *
   * `colour` is `null` when nobody has chosen one — a real state, rendered as
   * no swatch at all rather than as a grey square. `isWon` and `isLost` are
   * INDEPENDENT flags: both are carried, neither is derived from the other, and
   * neither is derived from `probability`.
   */
  colour?: string | null;
  probability?: number;
  isWon?: boolean;
  isLost?: boolean;
  /** Deal stages only: where a quote moves the deal (2026-10-18). */
  isQuoteSent?: boolean;
  /** Deal stages only: where a signed quote moves the deal (2026-10-27). */
  isQuoteSigned?: boolean;
  /**
   * Company stages only (migration 284). `undefined` on every other master and
   * on an older API — rendered as an em dash, never as an unticked box.
   */
  isClient?: boolean;
  isPastClient?: boolean;
  /** Company types only (migration 325). `undefined` = an older API: an em dash. */
  isPartner?: boolean;
  /** Price tiers only (migration 326). `undefined` = an older API: an em dash. */
  isDefault?: boolean;
  seed: MasterRecordSeed;
}

function referenceRow(record: ReferenceRecord): Row {
  return {
    id: record.id,
    name: record.name,
    code: null,
    dialling: null,
    popular: false,
    sortOrder: record.sortOrder,
    isActive: record.isActive,
    updatedAt: record.updatedAt,
    ...(record.isClient === undefined ? {} : { isClient: record.isClient }),
    ...(record.isPastClient === undefined ? {} : { isPastClient: record.isPastClient }),
    ...(record.isPartner === undefined ? {} : { isPartner: record.isPartner }),
    ...(record.isDefault === undefined ? {} : { isDefault: record.isDefault }),
    seed: record,
  };
}

function currencyRow(record: Currency): Row {
  return {
    id: record.id,
    name: record.currencyName,
    code: record.currencyCode,
    dialling: null,
    popular: false,
    sortOrder: null,
    isActive: record.isActive,
    updatedAt: record.updatedAt,
    seed: record,
  };
}

/**
 * A country's Currency cell, as the four states it can actually be in.
 *
 * ⚠️ **THE RECORD IS ASKED BEFORE THE MASTER IS.** `currencyId === null` is the
 * country's own answer and is true whatever the currency fetch is doing, so it
 * is decided first and reports `absent` even while the master is still out. A
 * country with no currency therefore never flickers through a skeleton on its
 * way to the em dash it was always going to show.
 *
 * The remaining three all mean "this country HAS a currency" and differ only in
 * what we managed to find out about it:
 *
 * - `pending` — nothing has come back. Say nothing.
 * - `unresolved` — the master failed, or arrived without this id. Both are a
 *   FAILED LOOKUP, not missing data, so the raw id is carried through rather
 *   than blanked. `perPage: 250` covers today's 164 currencies with headroom, so
 *   the second case should not arise — but "should not arise" is not a reason to
 *   render it as an em dash if it ever does, which is precisely the assumption
 *   the old code rested on.
 * - `value` — resolved, and the code is the master's own.
 */
function countryCurrencyCell(record: Country, currencies: MasterJoin<Currency>): JoinedCell {
  if (record.currencyId === null) return CELL_ABSENT;
  if (currencies.status === 'pending') return CELL_PENDING;
  const currency =
    currencies.status === 'ready' ? currencies.index.get(record.currencyId) : undefined;
  if (currency) return { kind: 'value', text: currency.currencyCode };
  return { kind: 'unresolved', text: `#${record.currencyId}` };
}

/**
 * @param currencies The currency master, as a `MasterJoin` — the INDEX PLUS THE
 * STATE OF THE FETCH THAT PRODUCED IT, never a bare `Map`. See `MasterJoin`: a
 * lookup that misses because the request has not landed and a country that has
 * no currency are different facts, and until 2026-08-31 this line collapsed them
 * into one em dash.
 *
 * TWO ADJACENT UPSTREAM IDS, ONE JOINABLE AND ONE NOT — and this is the only
 * place in the app where both are in scope, so both halves are spelled out:
 *
 * - `currencyId` **does** resolve against `/currencies`. Those ids are the
 *   upstream ones, verified against live data across all 195 countries, and the
 *   country read does not embed the currency object — so the join is fetch once,
 *   index by id, resolve locally, which is what happens here.
 * - `regionId` must **NEVER** be resolved against `/regions`. That endpoint is
 *   SEED's sales "Region of focus" — a completely unrelated id sequence — while
 *   this one is upstream geography. Both are integers, both are called region,
 *   and joining them yields plausible-looking nonsense rather than an error,
 *   which is precisely what makes it dangerous. It is not rendered in this table
 *   at all; the edit form shows the raw id, labelled as upstream and read-only.
 *   `continentId` is the same case.
 *
 * @param billingRegionNames `/billing-region-defaults` indexed by slug, for the
 * BILLING REGION column. A third local join, and the same shape as the currency
 * one above: fetch the master once, index it, resolve here.
 *
 * ⚠️ **The display name is the SERVER'S, never a map in this file.** The three
 * names — "United Kingdom", "Europe", "Rest of the World" — are columns on
 * `f_billing_region_defaults` that an admin may rewrite at any time. A literal
 * `{united_kingdom: 'United Kingdom', …}` here would be a second copy of wording
 * the server already owns, and it would go stale silently on the first rename.
 */
function countryRow(
  record: Country,
  currencies: MasterJoin<Currency>,
  billingRegionNames: Map<string, string>,
): Row {
  return {
    id: record.id,
    name: record.countryName,
    code: record.countryCode,
    dialling: record.countryCallingCode,
    currencyLabel: countryCurrencyCell(record, currencies),
    billingRegion: record.billingRegion,
    /*
     * FALL BACK TO THE RAW SLUG, never to blank and never to an em dash.
     *
     * Two states reach this line and both are recoverable reading:
     *  - the defaults master could not be read (its own request failed), so the
     *    index is empty;
     *  - the server sent a FOURTH slug this bundle's `CompanyBillingRegion`
     *    union does not know about.
     *
     * In both, the country genuinely HAS a billing region — the column is NOT
     * NULL server-side and all 195 rows carry one (measured 2026-08-31). An
     * `orDash` here, as the nullable columns beside it use, would say "this
     * country is unclassified", which is a different and false fact. The raw
     * slug is ugly and true, and it is also the exact string somebody needs in
     * order to go and look at why the name is missing.
     */
    billingRegionLabel: billingRegionNames.get(record.billingRegion) ?? record.billingRegion,
    popular: record.popularStudyDestination,
    sortOrder: null,
    isActive: record.isActive,
    updatedAt: record.updatedAt,
    seed: record,
  };
}

/**
 * A city row.
 *
 * ⚠️ **The region is read off the city and NEVER derived from its country.**
 * The shortcut — city → country → `Country.regionId` → `/regions` — is the one
 * mistake this endpoint invites, and it fails by looking correct: those are
 * upstream geography ids on an id sequence unrelated to SEED's Region of focus,
 * so since the master was cut to five values the join returns India correctly
 * and Egypt as "South East Asia", and cannot produce Latin America at all. A
 * city with `region: null` is unclassified and renders as unclassified.
 *
 * Both objects arrive resolved as `{id, name}`, so there is no local join to do
 * here — unlike `countryRow`, which has to index `/currencies` by id.
 */
function cityRow(record: City): Row {
  return {
    id: record.id,
    name: record.name,
    code: null,
    dialling: null,
    popular: false,
    sortOrder: record.sortOrder,
    isActive: record.isActive,
    updatedAt: record.updatedAt,
    countryLabel: record.country?.name ?? null,
    regionLabel: record.region?.name ?? null,
    seed: record,
  };
}

/**
 * A campaign inclusion, with BOTH stored unit forms shown.
 *
 * ⚠️ The two forms are rendered side by side rather than as one "campus(es)"
 * cell, and neither is ever computed from the other. `campus` → `campuses`
 * follows no rule worth writing — which is exactly why the API stores two
 * columns — and this is the screen an administrator checks before that text
 * prints on a customer's quote, so it must show what is actually stored.
 *
 * Both `null` is a real, supported configuration: the inclusion renders as a
 * bare name with no number. It reads "no unit" here rather than as an em dash,
 * because an em dash in this column would say "not filled in" about a
 * deliberate choice. One side missing is also legal and is left visible as an
 * em dash — that IS a half-filled pair, and quantities on that side will print
 * with no number at all.
 */
function inclusionRow(record: CampaignInclusion): Row {
  const singular = record.unitSingular;
  const plural = record.unitPlural;
  return {
    id: record.id,
    name: record.name,
    code: null,
    dialling: null,
    popular: false,
    sortOrder: record.sortOrder,
    isActive: record.isActive,
    updatedAt: record.updatedAt,
    unitLabel:
      singular === null && plural === null ? (
        <span className="text-fg-subtle">No unit — prints as a bare name</span>
      ) : (
        <span className="[overflow-wrap:anywhere]">
          <span className="text-fg-subtle">1</span> {orDash(singular)}
          <span className="text-fg-subtle"> · 2+</span> {orDash(plural)}
        </span>
      ),
    seed: record,
  };
}

/**
 * A deal stage.
 *
 * ⚠️ **Rendered in the order the endpoint returned it, and never sorted here.**
 * `GET /deal-stages` orders by `sort_order ASC, id ASC`, and that is the ONLY
 * thing carrying pipeline order: ids 1, 2 and 3 predate 4, 5 and 6, so the
 * running order is New Deal (1), Proposal Shared (4), Negotiation/Review (5),
 * Quote Shared (2), Invoiced (3), Closed Lost (6). Sort this list by id — or
 * alphabetically, which is the likelier accident — and the pipeline reads wrong
 * while looking perfectly tidy.
 *
 * There is no "Closed Won" in this pipeline and nothing here may imply one:
 * `Invoiced` is the won terminal and carries `isWon`.
 */
function dealStageRow(record: DealStage): Row {
  return {
    id: record.id,
    name: record.name,
    code: null,
    dialling: null,
    popular: false,
    sortOrder: record.sortOrder,
    isActive: record.isActive,
    updatedAt: record.updatedAt,
    colour: record.colour,
    probability: record.probability,
    isWon: record.isWon,
    isLost: record.isLost,
    isQuoteSent: record.isQuoteSent,
    isQuoteSigned: record.isQuoteSigned,
    seed: record,
  };
}

/**
 * A deal type or a lead source — the two remaining deal pickers, and the only
 * rows on this page whose shape looks like a reference master and is not one.
 *
 * The four fields this table renders are the four they have in common, so one
 * mapper covers both. What is deliberately NOT carried across is the pair these
 * rows have and the masters do not: `createdBy` and `modifiedBy` are resolved
 * `{id, name, email}` objects here, and neither is displayed, seeded into the
 * form or sent on a write — the server stamps both from the bearer token, and
 * both write schemas are `.strict()`, so offering either would be a 422 waiting
 * to happen.
 *
 * ⚠️ `GET` on both orders by `sort_order ASC, id ASC` — by ID on a tie, not by
 * name as the masters do. Rows render in the order received for exactly that
 * reason; re-sorting here would make this screen disagree with the dropdowns it
 * documents.
 */
function pickerRow(record: DealType | LeadSource): Row {
  return {
    id: record.id,
    name: record.name,
    code: null,
    dialling: null,
    popular: false,
    sortOrder: record.sortOrder,
    isActive: record.isActive,
    updatedAt: record.updatedAt,
    seed: record,
  };
}

// --- shared pieces ------------------------------------------------------

/**
 * Renders a `JoinedCell`. The three non-`value` branches are the point.
 *
 * - **pending** is a `Skeleton` — the house loading treatment, in the shape of
 *   the value it stands in for. Never a dash, which is what the cell shows for
 *   "none", and never a spinner. It carries an `sr-only` word because `Skeleton`
 *   is `aria-hidden`: a silent cell would put a screen-reader user straight back
 *   into the ambiguity this type exists to remove.
 * - **unresolved** prints the RAW IDENTIFIER rather than blanking. A read that
 *   failed is not an answer of "none", so it must not look like one — and the id
 *   is the exact string somebody needs in order to go and find out why the
 *   lookup missed. Same rule, and the same reasoning, as `countryRow` falling
 *   back to the raw billing-region slug a few lines away. Toned down so it does
 *   not read as a real currency code.
 * - **absent** is UNCHANGED and stays the em dash. This type was added to give
 *   the other three somewhere to go; it does not redefine what a record with no
 *   value shows.
 */
function JoinedValue({ value }: { value: JoinedCell | undefined }) {
  if (!value || value.kind === 'absent') return <>{orDash(null)}</>;
  if (value.kind === 'pending') {
    return (
      <>
        <Skeleton className="h-3 w-10" />
        <span className="sr-only">Loading</span>
      </>
    );
  }
  if (value.kind === 'unresolved') return <span className="text-fg-subtle">{value.text}</span>;
  return <>{value.text}</>;
}

/**
 * Active vs retired, as a word plus a dot — never colour alone. "Retired" is
 * spelled out rather than implied by a grey pill: a reader of this screen
 * specifically needs to know which values are no longer offered.
 */
function StatusBadge({ active }: { active: boolean }) {
  return <CatalogStatusBadge active={active} />;
}

function ErrorBlock({ children }: { children: ReactNode }) {
  return (
    <Notice tone="danger" role="alert">
      <span className="[overflow-wrap:anywhere]">{children}</span>
    </Notice>
  );
}

// --- cards --------------------------------------------------------------

function CardShell({
  icon,
  title,
  blurb,
  meta,
  action,
}: {
  icon: ReactNode;
  title: string;
  blurb?: string;
  meta: ReactNode;
  action: ReactNode;
}) {
  const headingId = useId();
  return (
    <Panel className="min-w-0" aria-labelledby={headingId}>
      <PanelBody className="flex h-full flex-col gap-3">
        <div className="flex items-start gap-2.5">
          <span aria-hidden="true" className="shrink-0 text-fg-subtle">
            {icon}
          </span>
          <div className="min-w-0">
            {/*
             * h3, not h2: the two section headings above these cards are the
             * h2s, so a card title is a level below the section it sits in.
             */}
            <h3 id={headingId} className="m-0 text-sm font-semibold text-fg">
              {title}
            </h3>
            {blurb && <p className="mt-0.5 text-xs text-fg-muted">{blurb}</p>}
          </div>
        </div>

        <div className="min-h-5">{meta}</div>

        <div className="mt-auto">{action}</div>
      </PanelBody>
    </Panel>
  );
}

/**
 * One managed master. The count and the retired count come from the same loaded
 * page the modal renders, so the card and the list can never disagree.
 */
function MasterCard({
  meta,
  rows,
  total,
  loading,
  error,
  canWrite,
  onOpen,
}: {
  meta: MasterMeta;
  rows: Row[];
  total: number;
  loading: boolean;
  error: string | null;
  canWrite: boolean;
  onOpen: () => void;
}) {
  const retired = rows.filter((row) => !row.isActive).length;

  return (
    <CardShell
      icon={meta.icon}
      title={meta.title}
      blurb={meta.blurb}
      meta={
        loading && rows.length === 0 ? (
          <Skeleton className="h-4 w-24" />
        ) : error ? (
          <span className="text-xs text-danger">Couldn’t load this list.</span>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone="neutral" variant="outline">{pluralise(total, 'value')}</Badge>
            {retired > 0 && (
              <Badge tone="neutral" variant="outline">
                {retired} retired
              </Badge>
            )}
          </div>
        )
      }
      action={
        <Button variant="ghost" size="sm" onClick={onOpen}>
          <span className="sr-only">
            {canWrite ? `Manage ${meta.title}` : `View ${meta.title}`}
          </span>
          <span aria-hidden="true">{canWrite ? 'Manage' : 'View'}</span>
        </Button>
      }
    />
  );
}

/**
 * Cycles and intakes, reading exactly as the six masters do — one card, one
 * Manage button, one modal. They keep their full pages for paging and column
 * filters, and the way through to those is the link in the modal footer rather
 * than a second, differently-shaped card here: a catalogue of eight lists where
 * six open and two navigate away was the thing that made this screen feel like
 * two screens.
 */
function PeriodCard({
  meta,
  total,
  loading,
  error,
  canWrite,
  onOpen,
}: {
  meta: PeriodMeta;
  total: number | null;
  loading: boolean;
  error: string | null;
  canWrite: boolean;
  onOpen: () => void;
}) {
  return (
    <CardShell
      icon={meta.icon}
      title={meta.title}
      blurb={meta.blurb}
      meta={
        loading && total === null ? (
          <Skeleton className="h-4 w-24" />
        ) : error ? (
          <span className="text-xs text-danger">Couldn’t load this list.</span>
        ) : (
          <Badge tone="neutral" variant="outline">
            {pluralise(total ?? 0, meta.singular)}
          </Badge>
        )
      }
      action={
        <Button variant="ghost" size="sm" onClick={onOpen}>
          <span className="sr-only">
            {canWrite ? `Manage ${meta.title}` : `View ${meta.title}`}
          </span>
          <span aria-hidden="true">{canWrite ? 'Manage' : 'View'}</span>
        </Button>
      }
    />
  );
}

/**
 * A CRM entity indexed here rather than managed here.
 *
 * The action is a LINK, not a Manage button, and the difference is the point.
 * Every other card on this page opens a modal that edits reference values in
 * place; a contact is a business record with its own page, its own filters and
 * its own detail route, and squeezing that into a modal would be a second,
 * worse implementation of a screen that already exists. So this card counts and
 * points, and `/contacts` still does the work.
 *
 * That is also why it lives under its own heading instead of in the grid
 * above: cycles and intakes were pulled into that grid so eight lists
 * wouldn't read as two screens, but a contact genuinely is a different kind
 * of thing, and pretending otherwise here would mislead instead of help.
 */
function EntityCard({
  icon,
  title,
  blurb,
  singular,
  total,
  loading,
  error,
  to,
  linkLabel,
}: {
  icon: ReactNode;
  title: string;
  blurb?: string;
  singular: string;
  total: number | null;
  loading: boolean;
  error: string | null;
  to: string;
  linkLabel: string;
}) {
  return (
    <CardShell
      icon={icon}
      title={title}
      blurb={blurb}
      meta={
        loading && total === null ? (
          <Skeleton className="h-4 w-24" />
        ) : error ? (
          <span className="text-xs text-danger">Couldn’t load the count.</span>
        ) : (
          <Badge tone="neutral" variant="outline">
            {pluralise(total ?? 0, singular)}
          </Badge>
        )
      }
      action={
        /*
         * Styled as the page's existing "open the full page" link rather than
         * as a Button: `Button` renders a <button> and is not polymorphic, and
         * a navigation dressed as a button loses middle-click, right-click and
         * the status bar. This is the same treatment `MasterListModal` gives
         * its footer link, so it is the page's own idiom and not a new one.
         */
        <TextLink to={to} className="inline-flex items-center gap-1 text-xs font-medium">
          {linkLabel}
          <ChevronIcon direction="right" />
        </TextLink>
      }
    />
  );
}

/**
 * The label above a band of cards. An `<h2>`, so the cards' own `<h3>` titles
 * nest under it and the page reads as two named groups rather than one long
 * undifferentiated grid.
 */
function SectionHeading({
  id,
  title,
  blurb,
}: {
  id: string;
  title: string;
  blurb?: string;
}) {
  return (
    <div className="mb-3">
      <h2
        id={id}
        className="m-0 text-base font-semibold text-fg"
      >
        {title}
      </h2>
      {blurb && <p className="mt-1 max-w-3xl text-xs text-fg-muted">{blurb}</p>}
    </div>
  );
}

// --- the manage modal ---------------------------------------------------

type PendingAction = {
  id: number;
  /* `flag` = a company stage's Client / Past client checkbox. */
  kind: 'activate' | 'deactivate' | 'delete' | 'flag';
} | null;

/**
 * ONE ORDERED KEY LIST PER SHAPE — the only description of this table's columns.
 *
 * ⚠️ **SUPERSEDED: `columnCount(shape, showActions)`**, a hand-kept integer whose
 * own comment promised it was "counted the same way `MasterHead` renders". That
 * promise could only ever be kept by somebody remembering it: the header, the
 * body and the count were three independent lists of `shape ===` conditions, and
 * a column added to two of the three throws nothing — the skeleton simply draws
 * a row a cell wide or narrow, on a loading state nobody reads twice. The head
 * and the body now render from THIS list and the count is `columns.length`, so
 * the three cannot disagree.
 */
type MasterColumnKey =
  | 'name'
  | 'colour'
  | 'odds'
  | 'outcome'
  | 'units'
  | 'country'
  | 'region'
  | 'order'
  | 'code'
  | 'dialling'
  | 'currency'
  | 'billingRegion'
  /* Company stages only — editable flag checkboxes, see `COMPANY_STAGE_COLUMNS`. */
  | 'client'
  | 'pastClient'
  /* Company types only — editable flag checkbox, see `COMPANY_TYPE_COLUMNS`. */
  | 'partner'
  /* Price tiers only — editable flag checkbox, see `PRICE_TIER_COLUMNS`. */
  | 'defaultTier'
  | 'updated'
  | 'status'
  | 'actions';

/*
 * The DECLARED order per shape — key for key, the order `MasterHead` rendered in
 * before this list existed. `actions` is appended by the caller, because whether
 * it exists at all depends on the reader's permissions rather than on the shape.
 *
 * A reader's stored order is applied over this by `useColumnLayout`, and a key
 * added here later lands in ITS DECLARED POSITION rather than at the end of the
 * table of everybody who has ever reordered one — which is the same reason that
 * hook stores the hidden set and never the visible one.
 *
 * Every entry is an explicit list rather than a base plus `shape !==` exceptions:
 * a negation over a growing union silently regains a column (a Code on cities,
 * say) the moment an eighth shape is added.
 */
const MASTER_COLUMNS: Record<Shape, readonly MasterColumnKey[]> = {
  reference: ['name', 'order', 'updated', 'status'],
  picker: ['name', 'order', 'updated', 'status'],
  currency: ['name', 'code', 'updated', 'status'],
  city: ['name', 'country', 'region', 'order', 'status'],
  inclusion: ['name', 'units', 'order', 'updated', 'status'],
  dealStage: ['name', 'colour', 'odds', 'outcome', 'order', 'status'],
  country: ['name', 'code', 'dialling', 'currency', 'billingRegion', 'status'],
};

/**
 * Company stages are a `reference` shape with two extra flag columns
 * (migration 284, owner decision 24): the "became a Client / Past Client"
 * notifications key on these flags, never on the stage name. Chosen by KIND
 * rather than as a new shape so the form modal and the column-layout scope
 * stay the reference ones.
 */
const COMPANY_STAGE_COLUMNS: readonly MasterColumnKey[] = [
  'name',
  'client',
  'pastClient',
  'order',
  'updated',
  'status',
];

/**
 * Company types: a `reference` shape with one flag column (migration 325,
 * owner 2026-10-27). Project capacity counts a company as Partner by this flag,
 * never by the type name. Chosen by KIND for the same reason as company stages.
 */
const COMPANY_TYPE_COLUMNS: readonly MasterColumnKey[] = [
  'name',
  'partner',
  'order',
  'updated',
  'status',
];

/** Price tiers: a `reference` shape with the one-default flag (migration 326). */
const PRICE_TIER_COLUMNS: readonly MasterColumnKey[] = [
  'name',
  'defaultTier',
  'order',
  'updated',
  'status',
];

function declaredColumnsFor(meta: MasterMeta): readonly MasterColumnKey[] {
  if (meta.kind === 'companyStages') return COMPANY_STAGE_COLUMNS;
  if (meta.kind === 'companyTypes') return COMPANY_TYPE_COLUMNS;
  if (meta.kind === 'priceTiers') return PRICE_TIER_COLUMNS;
  return MASTER_COLUMNS[meta.shape];
}

/**
 * 🔴 **ONLY THE GENUINELY WIDE MASTERS GET THE COLUMN MANAGER — the owner's
 * decision, applied here rather than re-derived.** Four of the seven shapes:
 * countries and deal stages at six data columns, inclusions and cities at five.
 *
 * ⛔ **The four-column shapes get NOTHING — not a disabled trigger, not a
 * greyed-out one.** Over `Value · Order · Updated · Status` a column manager can
 * only ever produce a worse table than the one it was opened on, so every
 * outcome it offers is an unhelpful one. That is the rule that keeps Xero's
 * `sync` button off the page and DocuSign's connect control out of its card: an
 * affordance whose only outcome is unhelpful is not shown. A disabled control
 * would also need a sentence explaining itself, which this app does not add.
 */
const MANAGED_SHAPES: readonly Shape[] = ['country', 'dealStage', 'inclusion', 'city'];

function isManagedShape(shape: Shape): boolean {
  return MANAGED_SHAPES.includes(shape);
}

/**
 * The Billing region filter's "no filter" wording, named once because the
 * `Combobox` reads it TWICE — as the clearing ROW inside the list, and as the
 * TRIGGER's text once nothing is selected. Two literals would agree today and
 * drift on the first rewording, leaving a cleared filter that reads as a blank
 * control rather than as "everything".
 */
const ALL_BILLING_REGIONS_LABEL = 'All billing regions';

/**
 * ⛔ **THE ACTIONS LANE IS LOCKED — AND SO IS THE NAME.**
 *
 * Actions, because hiding it takes Edit, Deactivate and Delete with it and there
 * is no second home for any of them. Opening `/companies` on its board once made
 * a "+" menu unreachable and the owner reported the feature as DELETED; a
 * missing affordance and a removed one are indistinguishable to a reader.
 *
 * The name, because it is the row's `<th scope="row">`. Hidden, every row loses
 * its header: a screen reader announces figures belonging to nobody, and on
 * screen the table becomes a grid of codes and dates with a Delete button on the
 * end. The confirmation names the row it is about to remove permanently, and the
 * reader would have nothing in front of them to check it against.
 *
 * ⚠️ **A locked key is still LISTED in the manager**, disabled, with the reason
 * on its `title` — that being the one sentence the no-helper-text prohibition's
 * own exception list allows. Omitted, it would read as the manager having lost a
 * column rather than as a column it refuses to touch. The two sentences are
 * supplied per key through `lockReasonOf`; the component's fallback says the
 * column "cannot be hidden", which is true of both and explains neither.
 *
 * ⛔ **LOCKED IS NOT PINNED, AND NEITHER OF THESE IS PINNED.** Locked means this
 * table will not hide or reorder the column; it says nothing about which edge
 * the column sits at. Nothing on this page renders `pinned`, so **both of these
 * are freezable to either edge and are meant to be** — `name` most of all, being
 * the column a reader most wants held at the left while a six-column table
 * scrolls under it. There is no `pinnedKeys` to pass here for the same reason.
 */
const LOCKED_MASTER_COLUMNS: readonly MasterColumnKey[] = ['name', 'actions'];

/*
 * The header-cell widths that shipped, key for key, and they stay LITERAL
 * strings: Tailwind v4 scans source text, so a computed `w-${n}` emits no rule
 * at all — the class lands in the DOM and does nothing, which looks exactly like
 * a column that chose not to take a width.
 *
 * `name` carries none, as it always has: it is the column the auto layout is
 * meant to give the remainder to.
 */
const MASTER_TH_CLASS: Partial<Record<MasterColumnKey, string>> = {
  colour: 'w-28',
  odds: 'w-20',
  outcome: 'w-28',
  units: 'w-64',
  country: 'w-44',
  region: 'w-44',
  order: 'w-20',
  code: 'w-20',
  dialling: 'w-24',
  currency: 'w-24',
  billingRegion: 'w-40',
  client: 'w-24',
  pastClient: 'w-28',
  partner: 'w-36',
  defaultTier: 'w-28',
  updated: 'w-28',
  status: 'w-28',
  actions: 'w-52',
};

const MASTER_COLUMN_LABELS: Record<Exclude<MasterColumnKey, 'name'>, string> = {
  /*
   * The hex, spelled out. A swatch alone would be colour as the only signal, and
   * this is the screen somebody opens specifically to check what a stage is set
   * to before changing it — so the value is readable, copyable text and the
   * square beside the name is the illustration.
   */
  colour: 'Colour',
  odds: 'Odds',
  /* Won and lost are independent flags, so this column can legitimately show
     both — it is not a one-of-three outcome. */
  outcome: 'Outcome',
  /*
   * ONE column carrying BOTH stored forms, labelled so the reader can see which
   * is which. Not "Unit", singular: there are two, and a header implying one is
   * how somebody concludes the other is generated.
   */
  units: 'Units — 1 · 2 or more',
  country: 'Country',
  region: 'Region of focus',
  order: 'Order',
  code: 'Code',
  dialling: 'Dialling',
  currency: 'Currency',
  /*
   * Read-only, and there is no sort control on it — nor on any header in this
   * table. See `MasterListModal`'s own note: these rows render in the order the
   * endpoint returned them because that is the order the pickers use, and
   * `/countries` accepts no `sort` at all (a `?sort=` key is a 422 on a
   * `.strict()` query, measured 2026-08-31). A lone sortable header among six
   * plain ones would also be an affordance this table has never offered.
   */
  billingRegion: 'Billing region',
  client: 'Client',
  pastClient: 'Past client',
  partner: 'Partner category',
  defaultTier: 'Default tier',
  updated: 'Updated',
  status: 'Status',
  actions: 'Actions',
};

function masterColumnLabel(shape: Shape, key: MasterColumnKey): string {
  if (key !== 'name') return MASTER_COLUMN_LABELS[key];
  return shape === 'country'
    ? 'Country'
    : shape === 'city'
      ? 'City'
      : shape === 'dealStage'
        ? 'Stage'
        : 'Value';
}

/**
 * The `min-w-` floor of a managed table, as one SHARE per column.
 *
 * ⚠️ **THESE ARE SHARES OF THE SHIPPED FLOOR, NOT INDEPENDENT MEASUREMENTS, and
 * each shape's shares sum to EXACTLY the literal that stood here before** — 920
 * countries, 820 deal stages, 780 inclusions, 720 cities. So a table with every
 * column shown renders at the width it has always rendered at, and hiding one
 * subtracts that column's share and nothing else. A reader who never opens the
 * manager sees no change at all.
 *
 * ⚠️ **They are deliberately NOT the `w-` requests above.** This table has no
 * `table-fixed` and no `<colgroup>`, so a `w-` is a request the auto layout may
 * overrule from content — and on cities and inclusions the shipped floor is
 * NARROWER than those requests add up to, so reusing them would have widened two
 * tables nobody asked to widen. Where the floor is wider (countries) the surplus
 * goes to `name`, which is the column with no width of its own.
 *
 * ⛔ **A number, through `style`, never a class** — see `MASTER_TH_CLASS`.
 */
const MASTER_COLUMN_WIDTHS: Partial<Record<Shape, Partial<Record<MasterColumnKey, number>>>> = {
  country: {
    name: 168,
    code: 80,
    dialling: 96,
    currency: 96,
    billingRegion: 160,
    status: 112,
    actions: 208,
  },
  dealStage: {
    name: 140,
    colour: 108,
    odds: 76,
    outcome: 108,
    order: 76,
    status: 104,
    actions: 208,
  },
  inclusion: { name: 144, units: 216, order: 72, updated: 100, status: 100, actions: 148 },
  city: { name: 144, country: 132, region: 132, order: 72, status: 100, actions: 140 },
};

/**
 * The floor for the columns actually on screen, or `undefined` for a shape that
 * keeps the literal it shipped with.
 *
 * The unmanaged shapes are left on their class deliberately: nothing can hide a
 * column there, so there is nothing for a computed floor to express.
 */
function masterTableMinWidth(
  shape: Shape,
  columns: readonly MasterColumnKey[],
): number | undefined {
  const widths = MASTER_COLUMN_WIDTHS[shape];
  if (widths === undefined) return undefined;
  return columns.reduce((total, key) => total + (widths[key] ?? 0), 0);
}

/*
 * ⚠️ **SUPERSEDED — `useHeadRowRef`, a container ref plus a `querySelector('thead
 * tr')` handing out a FRESH ref object whenever the row element changed.** It
 * existed for two reasons and both are now answered in the shared pieces:
 *
 * - `TR` took no `ref`, so the header row could not be addressed directly. It
 *   does now, so `useRef` + `<TR ref={…}>` says the same thing in one line.
 * - `useFrozenOffsets` re-measured on the IDENTITY OF THE REF it was handed, so
 *   a stable ref mutated in place would have gone on measuring — and observing —
 *   the row detached when the skeleton table gave way to the real one. The hook
 *   tracks the observed ELEMENT itself now (`observedRow`), which is strictly
 *   better: it catches the swap whether or not a caller thought to signal it.
 *
 * Both tables here render the skeleton and the loaded rows as two separate
 * `<Table>` mounts, so that swap is the ordinary path rather than an edge — it
 * is the case the shared fix was written for, and there is nothing left for a
 * caller to do about it.
 */

/**
 * How wide the list modal opens, by shape.
 *
 * 🔴 **`lg` COULD NOT SEAT THE COUNTRY TABLE EVEN BEFORE THE BILLING REGION
 * COLUMN, and the clipped control was the DESTRUCTIVE one.** The arithmetic,
 * from source rather than from taste: `Modal`'s `lg` is `sm:max-w-3xl` = 768px,
 * its body is `px-4` and the panel carries a 1px border, so the content box is
 * `768 − 32 − 2 = 734px`. The country table's floor was already
 * `min-w-[760px]` — 26px wider than the box it sits in, before any cell's
 * content demand pushed it further. `TableWrap` scrolls, so nothing errored:
 * the Actions column simply fell off the right edge and "Deactivate" rendered
 * as "Deactiv", reported at ~1250px browser width.
 *
 * A label cut in half on a destructive control is not a cosmetic issue, and
 * appending a sixth data column to a row that already does not fit would have
 * made an existing, visible defect worse. `Modal`'s own doc says `xl` is the
 * reach "when the content is a table" — this content IS a table — so the wide
 * shapes take it: `sm:max-w-7xl` = 1280px, a content box of ~1246px.
 *
 * ⚠️ **`inclusion` (780) and `dealStage` (820) are here because their floors
 * fail the SAME arithmetic**, not because either was driven and found broken.
 * More room cannot clip more, so widening them is safe — but do not read their
 * inclusion as a claim that they were verified. `city` (720) and the plain
 * shapes (600) fit inside 734 and are deliberately left at `lg`: a five-column
 * reference list spread over 1246px is a regression nobody asked for.
 */
function modalSizeFor(shape: Shape): ModalSize {
  return shape === 'country' || shape === 'inclusion' || shape === 'dealStage' ? 'xl' : 'lg';
}

/**
 * The header, rendered from the live column list rather than from a bank of
 * `shape ===` conditions. The labels, the `w-` requests and the order all live
 * in the model above, so this and the body row below cannot describe different
 * tables.
 */
function MasterHead({
  shape,
  columns,
  frozenSideOf,
  frozenOffsets,
  headRowRef,
}: {
  shape: Shape;
  columns: readonly MasterColumnKey[];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
  /* `useFrozenOffsets` MEASURES this row: there is no `table-fixed` here, so a
     `w-*` on a `<th>` is a request the content can outvote and the rendered
     width of a column exists only in the DOM. */
  headRowRef: RefObject<HTMLTableRowElement | null>;
}) {
  return (
    <THead>
      <TR ref={headRowRef}>
        {columns.map((key) => (
          <TH
            key={key}
            numeric={key === 'odds' || key === 'order'}
            frozen={frozenSideOf(key)}
            frozenOffset={frozenOffsets[key]}
            className={MASTER_TH_CLASS[key]}
          >
            {/*
              * The actions lane has never carried a visible heading — the
              * controls under it label themselves — but it still needs an
              * accessible one, or the column manager's own row for it would be
              * the only place its name appears.
              */}
            {key === 'actions' ? (
              <span className="sr-only">{masterColumnLabel(shape, key)}</span>
            ) : (
              masterColumnLabel(shape, key)
            )}
          </TH>
        ))}
      </TR>
    </THead>
  );
}

/**
 * One value cell — everything except the name, which is a `<th scope="row">`,
 * and the actions lane, which closes over the modal's own handlers.
 *
 * `switch` on the key with no `default`, so a column added to `MasterColumnKey`
 * and to `MASTER_COLUMNS` but not to this file fails at `tsc` rather than
 * rendering an empty cell under a populated header.
 */
function MasterValueCell({
  columnKey,
  row,
  frozen,
  frozenOffset,
}: {
  columnKey: Exclude<
    MasterColumnKey,
    'name' | 'actions' | 'client' | 'pastClient' | 'partner' | 'defaultTier'
  >;
  row: Row;
  frozen: FrozenSide | undefined;
  frozenOffset: number | undefined;
}) {
  const edge = { frozen, frozenOffset };

  switch (columnKey) {
    /*
     * Two independent facts, side by side, and neither is computed from the
     * other. An em dash in the Region column means the city is unclassified — it
     * does NOT mean the country failed to tell us, and nothing here falls back
     * to the country to fill it in.
     */
    case 'country':
      return (
        <TD {...edge} className="text-xs text-fg-muted">
          <span className="[overflow-wrap:anywhere]">{orDash(row.countryLabel ?? null)}</span>
        </TD>
      );

    case 'region':
      return (
        <TD {...edge} className="text-xs text-fg-muted">
          <span className="[overflow-wrap:anywhere]">{orDash(row.regionLabel ?? null)}</span>
        </TD>
      );

    /*
     * BOTH stored forms, never one plus a derived other. `campus` → `campuses`
     * follows no rule worth writing, which is why there are two columns behind
     * this cell — and this text prints on a customer's quote, so the screen that
     * documents it must show what is stored.
     */
    case 'units':
      return (
        <TD {...edge} className="text-xs text-fg-muted">
          {row.unitLabel}
        </TD>
      );

    /*
     * THE HEX AS TEXT, with the swatch already beside the name. `StageSwatch`
     * refuses anything but `#rrggbb`, so a stage with no colour shows no square —
     * and this cell says "No colour" in words rather than leaving an em dash that
     * would read as "failed to load".
     */
    case 'colour':
      return (
        <TD {...edge} className="text-xs whitespace-nowrap text-fg-muted tabular">
          {row.colour ? row.colour : <span className="text-fg-subtle">No colour</span>}
        </TD>
      );

    case 'odds':
      return (
        <TD {...edge} numeric className="text-xs text-fg-muted tabular">
          {row.probability}%
        </TD>
      );

    /*
     * Both flags, read in a fixed order and neither inferred from the other.
     * "Open" is the normal answer for everything mid-pipeline and is said in
     * words rather than left blank. A stage carrying BOTH is legal — the API
     * allows it — so it is shown as both rather than collapsed into one, which
     * would hide the mistake.
     */
    case 'outcome':
      return (
        <TD {...edge}>
          <div className="flex flex-wrap gap-1">
            {row.isWon && <Badge tone="success">Won</Badge>}
            {row.isLost && <Badge tone="danger">Lost</Badge>}
            {!row.isWon && !row.isLost && <span className="text-xs text-fg-subtle">Open</span>}
            {/* The quote-sent target (2026-10-18). Absent on an older API, so only `true` shows. */}
            {row.isQuoteSent === true && <Badge tone="info">Quote sent</Badge>}
            {/* The quote-signed target (2026-10-27). Same rule: only `true` shows. */}
            {row.isQuoteSigned === true && <Badge tone="info">Quote signed</Badge>}
          </div>
        </TD>
      );

    case 'order':
      return (
        <TD {...edge} numeric className="text-xs text-fg-muted tabular">
          {row.sortOrder}
        </TD>
      );

    case 'code':
      return (
        <TD {...edge} className="text-xs whitespace-nowrap text-fg-muted tabular">
          {orDash(row.code)}
        </TD>
      );

    case 'dialling':
      return (
        <TD {...edge} className="text-xs whitespace-nowrap text-fg-muted tabular">
          {orDash(row.dialling)}
        </TD>
      );

    /*
     * FOUR STATES, not two — see `JoinedCell`. This is the one cell on the page
     * resolved against a master fetched by a separate request, so it is the one
     * cell where "nothing came back yet" and "this country has no currency"
     * could be told apart and were not: `orDash` over a missed lookup printed
     * the em dash that means "none". Two browser runs against identical data
     * disagreed — `—` at 1440, `AFN` at 1280 — because one read the table before
     * the currency fetch landed.
     *
     * The em dash is unchanged for a country that really has no currency. It is
     * the other three states that now have somewhere else to go.
     */
    case 'currency':
      return (
        <TD {...edge} className="text-xs whitespace-nowrap text-fg-muted tabular">
          <JoinedValue value={row.currencyLabel} />
        </TD>
      );

    /*
     * NO `orDash` HERE, unlike its three neighbours, and the difference is
     * deliberate rather than an oversight.
     *
     * Code, dialling and currency are genuinely nullable and an em dash on any of
     * them means "not recorded". `billing_region` is a NOT NULL ENUM and all 195
     * rows carry one (measured 2026-08-31), so there is no unclassified state for
     * a dash to describe — and `countryRow` guarantees a non-empty string by
     * falling back to the raw slug. A dash in this column would announce missing
     * data that does not exist.
     *
     * Not `tabular` either: this is a name, not a figure.
     */
    case 'billingRegion':
      return (
        <TD {...edge} className="text-xs text-fg-muted">
          <span className="[overflow-wrap:anywhere]">{row.billingRegionLabel}</span>
        </TD>
      );

    case 'updated':
      return (
        <TD {...edge} className="text-xs whitespace-nowrap text-fg-muted">
          {formatDateOnly(row.updatedAt)}
        </TD>
      );

    case 'status':
      return (
        <TD {...edge}>
          <StatusBadge active={row.isActive} />
        </TD>
      );
  }
}

/**
 * The list behind a card: every row of one master, active and retired together.
 *
 * There is no separate archive screen and no "hide retired" toggle, because an
 * unfiltered GET returns both and a retired row is the thing a reader most often
 * came here to look up. Rows render in the order the API returned them —
 * `sortOrder` ASC then name — which is the order the pickers use, so re-sorting
 * here would make this screen disagree with the control it documents.
 */
function MasterListModal({
  meta,
  open,
  onClose,
  rows,
  loading,
  error,
  ability,
  onReload,
}: {
  meta: MasterMeta;
  open: boolean;
  onClose: () => void;
  rows: Row[];
  loading: boolean;
  error: string | null;
  ability: Ability;
  onReload: () => void;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MasterRecordSeed | null>(null);
  const [pending, setPending] = useState<PendingAction>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Row | null>(null);
  /*
   * `ReactNode`, not `string`: a deal stage's 409 is paired with a link to the
   * deals holding it, because that refusal cannot be acted on from inside this
   * list. Every other master still sets a plain string, which is a ReactNode.
   */
  const [deleteError, setDeleteError] = useState<ReactNode>(null);

  /**
   * The Billing region filter — countries only. `''` is "all", and it is the
   * SLUG that is stored, never the label.
   *
   * ⛔ **CLIENT-SIDE, AND IT CANNOT BE ANYTHING ELSE.** `/countries` refuses
   * both keys somebody would reach for: `?billingRegion=europe` is a **422
   * `Unrecognized key: "billingRegion"`** and `?sort=` is a 422 the same way —
   * the list query is `.strict()`, so neither may ever be sent (measured
   * 2026-08-31). Filtering here is exact rather than an approximation only
   * because this master is loaded with `WIDE_QUERY` (`perPage: 250`) against
   * 195 rows, so the modal holds the WHOLE list in one page. **If that ceiling
   * is ever crossed this control silently starts filtering a first page and
   * calling it the answer** — `TruncationNotice` is what would say so.
   *
   * ⚠️ **`''` IS THE ABSENT STATE IN THE STORE AND IN THE CONTROL ALIKE**, which
   * is the whole reason the 2026-09-13 move from a native `<select>` to a
   * searchable `Combobox` changed no behaviour: the "All billing regions" row
   * left the option list and became `emptyOptionLabel`, and the empty string it
   * always carried is what a `Combobox` already means by "nothing selected".
   */
  const [billingRegionFilter, setBillingRegionFilter] = useRememberedState(
    'countries-billing-region',
    '',
  );
  const billingRegionFilterId = useId();

  /*
   * Options are derived from the rows on screen rather than from the
   * `/billing-region-defaults` master, which is not in scope here. Two things
   * follow, both wanted: the control can only ever offer a region that is
   * actually present, so a selection cannot produce an empty table; and where
   * the label failed to resolve, the option reads the same raw slug the cell
   * does rather than disagreeing with it.
   *
   * Sorted by label. The master carries its own `sortOrder`, but that is not on
   * a country row and inventing an order out of country data would be a claim
   * about the business this screen cannot support — a three-item lookup list is
   * not a pipeline, so alphabetical is the honest ordering.
   */
  const billingRegionOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of rows) {
      if (row.billingRegion && !seen.has(row.billingRegion)) {
        seen.set(row.billingRegion, row.billingRegionLabel ?? row.billingRegion);
      }
    }
    return [...seen].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  /*
   * Several regions at once (owner 2026-10-28): the remembered value is a
   * comma-separated list of slugs, so a single slug remembered before reads
   * back as a one-item selection. Matched as a set.
   */
  const billingRegionSelected = useMemo(
    () => (billingRegionFilter ? billingRegionFilter.split(',') : []),
    [billingRegionFilter],
  );
  const filtering = meta.shape === 'country' && billingRegionSelected.length > 0;
  const visibleRows = useMemo(() => {
    if (!filtering) return rows;
    const wanted = new Set(billingRegionSelected);
    return rows.filter((row) => Boolean(row.billingRegion) && wanted.has(row.billingRegion ?? ''));
  }, [rows, filtering, billingRegionSelected]);

  /*
   * 2026-10-02: each master asks its own catalogue module (`MASTER_MODULE`).
   * *SUPERSEDED: `reference-data` for every master, "admin-only server-side".*
   */
  const masterModule = MASTER_MODULE[meta.kind];
  const canCreate = ability.canCreate(masterModule);
  const canUpdate = ability.canUpdate(masterModule);
  const canDelete = ability.canDelete(masterModule);
  const updateDenial = denialReason(ability, 'update', masterModule);

  const showActions = canUpdate || canDelete;

  /*
   * The DECLARED columns for this shape, with the actions lane appended only
   * when this reader has a control to put in it. That is the list the manager
   * shows and the list `useColumnLayout` orders — never the visible one, or a
   * reader with no write permission would be offered a lane that is not there.
   */
  const declaredColumns = useMemo<readonly MasterColumnKey[]>(
    () =>
      showActions ? [...declaredColumnsFor(meta), 'actions' as const] : declaredColumnsFor(meta),
    [meta, showActions],
  );

  const managed = isManagedShape(meta.shape);

  /*
   * ⚠️ **CALLED FOR EVERY SHAPE AND READ FOR FOUR, because a hook cannot be
   * conditional.** The cost is one `GET /preferences` when somebody opens a
   * narrow list — one request, on a user action, on the only list modal mounted
   * at a time — and the hook degrades to the default layout when it fails, so
   * nothing about the table depends on it answering.
   *
   * ⛔ **Its result is IGNORED on the three narrow shapes, and that is not
   * tidiness.** They render no manager, so a layout stored against their scope —
   * which nothing in this app can write today — would hide columns the reader
   * has no control to bring back. A stored preference may only ever subtract
   * where there is something on screen to undo it with.
   *
   * ⚠️ **The scope is the SHAPE, not the master.** One renderer serves fourteen
   * lists and the column identity belongs to the shape: `sortOrder` means the
   * same thing on regions and on booking statuses, and a reader who arranges the
   * cities table has arranged the only table with those columns. Scoping per
   * master would make the same arrangement fourteen separate preferences, each
   * to be set again.
   */
  const layout = useColumnLayout(`catalog.${meta.shape}`, declaredColumns, LOCKED_MASTER_COLUMNS);

  const columns = (managed ? layout.visibleKeys : declaredColumns) as readonly MasterColumnKey[];

  /* Destructured so the memo depends on the stable callback rather than on the
     whole controller, which is a new object on every render. */
  const { frozenSideOf: layoutFrozenSideOf } = layout;
  const frozenSideOf = useCallback(
    (key: string) => (managed ? layoutFrozenSideOf(key) : undefined),
    [managed, layoutFrozenSideOf],
  );

  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, columns, frozenSideOf);

  const columnLabelOf = useCallback(
    (key: string) => masterColumnLabel(meta.shape, key as MasterColumnKey),
    [meta.shape],
  );

  /** Why each locked column is locked, in the reader's own terms — the one kind
   *  of sentence the no-helper-text rule keeps, because a bare `disabled` makes a
   *  locked column and a broken switch identical. See `LOCKED_MASTER_COLUMNS`. */
  const columnLockReason = useCallback((key: string): string | undefined => {
    if (key === 'name') {
      return 'The row’s own heading — hidden, every row here becomes codes and dates belonging to nobody, including the one a delete confirms';
    }
    if (key === 'actions') {
      return 'Edit, deactivate and delete live here and nowhere else on this screen';
    }
    return undefined;
  }, []);

  /*
   * ⚠️ **SUPERSEDED — `containPopoverEscape`, a wrapper `onKeyDown` that stopped
   * the synthetic Escape before `Modal` saw it and re-raised a non-bubbling
   * native `keydown` at `document`.** It was written because `ColumnManager`
   * bound its listener in the BUBBLE phase: React delegates from the portal
   * container, `document.body`, one node BELOW `document`, so `Modal`'s
   * `onKeyDown` ran first, closed this dialog, and its own `stopPropagation()`
   * — which stops the NATIVE event too — meant the popover's listener never ran
   * at all. Escape over an open popover closed the whole modal.
   *
   * ✅ **`ColumnManager` binds in the CAPTURE phase now, which is the real fix
   * and is strictly better than what was worked around here.** Capture runs
   * `document` BEFORE `body`, so the popover's handler fires first and its
   * `stopPropagation()` keeps the key from ever reaching React's delegated
   * listener — the modal never learns the key was pressed. The listener is bound
   * only while the popover is open, so a closed popover intercepts nothing and
   * Escape still closes this dialog exactly as every other control in it does.
   *
   * ⚠️ **The workaround was not merely redundant, it was already unreachable**:
   * the capture handler stops the native event before React can dispatch a
   * synthetic one, so `onKeyDown` here could no longer run. Left in place it
   * would have read as the thing doing the work.
   */

  const initialLoad = loading && rows.length === 0;
  const busy = pending !== null;
  const skeletonRows = Array.from({ length: SKELETON_ROWS }, (_, index) => index);
  /*
   * The LIVE count. It was a hand-kept integer; it is now the length of the list
   * the header and the body both render from, so a skeleton row can no longer be
   * a cell wide or narrow, and it narrows with the reader's hidden columns.
   */
  const skeletonCells = Array.from({ length: columns.length }, (_, index) => index);
  const tableMinWidth = masterTableMinWidth(meta.shape, columns);

  function reset() {
    setActionError(null);
    setDeleting(null);
    setDeleteError(null);
    setPending(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  /** The one write that is meant to be easy. Reversible, so it is not confirmed. */
  async function toggleActive(row: Row) {
    if (busy) return;
    const next = !row.isActive;
    setPending({ id: row.id, kind: next ? 'activate' : 'deactivate' });
    setActionError(null);
    try {
      if (meta.shape === 'reference') {
        await api.updateReference(meta.kind as ReferenceMasterKind, row.id, { isActive: next });
      } else if (meta.shape === 'currency') {
        await api.updateCurrency(row.id, { isActive: next });
      } else if (meta.shape === 'city') {
        /*
         * `isActive` alone, and that is the whole point of the retire path: a
         * city's `regionId` is re-checked against the ACTIVE regions on every
         * write, so a patch that helpfully re-sent the region a city already
         * holds would 422 on a field nobody touched the day that region was
         * retired. One key in, one column changed.
         */
        await api.updateCity(row.id, { isActive: next });
      } else if (meta.shape === 'inclusion') {
        /*
         * `isActive` alone, for the same reason a city sends only `isActive`:
         * an omitted key is untouched, and re-sending the two unit forms from a
         * row the reader never opened is how a retire quietly rewrites the text
         * that prints on a customer's quote.
         */
        await api.updateCampaignInclusion(row.id, { isActive: next });
      } else if (meta.shape === 'dealStage') {
        /*
         * `isActive` alone, and on this master the reason has teeth in two
         * directions. `colour` distinguishes omitted from null — re-sending a
         * `colour: null` read off a row nobody opened would wipe the swatch —
         * and `probability` has no default on PATCH, so re-sending it from a
         * stale row would rewrite the number the forecast reads. One key in,
         * one column changed.
         */
        await api.updateDealStage(row.id, { isActive: next });
      } else if (meta.shape === 'picker') {
        /*
         * `isActive` alone, and the endpoint is chosen by KIND rather than by
         * shape — deal types and lead sources share this shape and are two
         * different tables. `/lead-sources` is top-level and shared, so it is
         * not reachable through anything deal-scoped.
         */
        if (meta.kind === 'dealTypes') await api.updateDealType(row.id, { isActive: next });
        else await api.updateLeadSource(row.id, { isActive: next });
      } else {
        await api.updateCountry(row.id, { isActive: next });
      }
      onReload();
    } catch (caught) {
      setActionError(
        describeWriteError(caught, meta.singular, next ? 'reactivate' : 'deactivate', updateDenial),
      );
    } finally {
      setPending(null);
    }
  }

  /**
   * A company stage's Client / Past client flag. One key per PATCH, like
   * `toggleActive`: re-sending the other flag from a row nobody opened would
   * overwrite a change made since. Reversible from the same box, so not confirmed.
   */
  async function toggleStageFlag(
    row: Row,
    flag: 'isClient' | 'isPastClient' | 'isPartner' | 'isDefault',
  ) {
    // Company types carry `isPartner` (migration 325); price tiers `isDefault`
    // (migration 326; the server moves it, and a 409 on clearing the holder
    // lands in the action error); company stages the other two.
    const kind =
      flag === 'isPartner' ? 'companyTypes' : flag === 'isDefault' ? 'priceTiers' : 'companyStages';
    if (busy || meta.kind !== kind) return;
    const next = !(row[flag] ?? false);
    setPending({ id: row.id, kind: 'flag' });
    setActionError(null);
    try {
      // A computed key widens to an index signature, so `isPartner` (which the
      // shared input type does not name) passes without a cast.
      await api.updateReference(kind, row.id, { [flag]: next });
      onReload();
    } catch (caught) {
      setActionError(describeWriteError(caught, meta.singular, 'update', updateDenial));
    } finally {
      setPending(null);
    }
  }

  async function confirmDelete() {
    if (!deleting || busy) return;
    setPending({ id: deleting.id, kind: 'delete' });
    setDeleteError(null);
    try {
      if (meta.shape === 'reference') {
        await api.deleteReference(meta.kind as ReferenceMasterKind, deleting.id);
      } else if (meta.shape === 'currency') {
        await api.deleteCurrency(deleting.id);
      } else if (meta.shape === 'city') {
        await api.deleteCity(deleting.id);
      } else if (meta.shape === 'inclusion') {
        await api.deleteCampaignInclusion(deleting.id);
      } else if (meta.shape === 'dealStage') {
        await api.deleteDealStage(deleting.id);
      } else if (meta.shape === 'picker') {
        if (meta.kind === 'dealTypes') await api.deleteDealType(deleting.id);
        else await api.deleteLeadSource(deleting.id);
      } else {
        // Unreachable: `deletable` is false for countries, so no control exists
        // to get here. Explicit rather than falling through to another endpoint.
        throw new Error('Deleting a country is not supported.');
      }
      setDeleting(null);
      onReload();
    } catch (caught) {
      /*
       * A 409 means something references this row TODAY. It is the only
       * protection there is, and it is exactly the case where deactivating is
       * the right move — so the message says so instead of just relaying the
       * status.
       */
      if (caught instanceof ApiError && caught.status === 409) {
        /*
         * Switched on the STATUS, never on the message text — the server's
         * wording names counts and is free to change. A deal stage gets the one
         * route onward as well: its refusal names how many deals hold the stage
         * and `/deals?view=table&stageId=N` is exactly that set, which the
         * reader cannot reach from inside this modal.
         *
         * `view=table` is explicit because /deals now opens on its board. The
         * job here is to ENUMERATE the deals holding this stage against a count
         * the refusal just quoted — so the reader needs rows and a total, not a
         * single stage column capped at 100 cards with the rest behind another
         * link. Grouping by stage is also meaningless on a set filtered to one
         * stage.
         */
        setDeleteError(
          meta.shape === 'dealStage' ? (
            <>
              {caught.message}{' '}
              <Link
                to={`/deals?view=table&stageId=${deleting.id}`}
                className="font-medium underline underline-offset-2"
              >
                Review those deals
              </Link>{' '}
              first, or deactivate the stage instead — the deals on it keep working, and it stops
              being offered.
            </>
          ) : (
            `${caught.message} Deactivate it instead — the records using it keep working, and it stops being offered.`
          ),
        );
      } else {
        setDeleteError(
          describeWriteError(
            caught,
            meta.singular,
            'delete',
            denialReason(ability, 'delete', masterModule),
          ),
        );
      }
    } finally {
      setPending(null);
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={meta.title}
        size={modalSizeFor(meta.shape)}
        footer={
          <>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            {canCreate && (
              <Button
                variant="primary"
                leadingIcon={<PlusIcon />}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                New {meta.singular}
              </Button>
            )}
          </>
        }
      >
        <div className="grid gap-3">
          {error && <ErrorBlock>Couldn’t load {meta.plural}. {error}</ErrorBlock>}
          {actionError && <ErrorBlock>{actionError}</ErrorBlock>}

          {updateDenial && (
            <p className="m-0 text-xs text-fg-subtle">{updateDenial} You can read it here.</p>
          )}

          {/*
            * The one filter on this screen, and it is here rather than in a
            * `FilterableTH` on the header: those claim `aria-sort` and are
            * driven by `use-table-filters` writing to the URL, and this list
            * lives inside a modal with no route of its own. A labelled control
            * above the table is the honest shape for an in-memory narrow.
            *
            * Rendered only when there is more than one region to choose
            * between — a filter offering a single option narrows nothing and is
            * an affordance with one outcome. It is absent while the list is
            * still loading or failed, where it would offer to narrow nothing.
            */}
          {(managed || (meta.shape === 'country' && billingRegionOptions.length > 1)) && (
            <div className="flex flex-wrap items-center gap-2">
              {meta.shape === 'country' && billingRegionOptions.length > 1 && (
                <>
                  <label
                    htmlFor={billingRegionFilterId}
                    className="text-xs font-medium text-fg-secondary"
                  >
                    Billing region
                  </label>
                  {/*
                    * ⚠️ **SIZED BY THE WRAPPER, AND THE WRAPPER IS STILL
                    * REQUIRED — the reason changed on 2026-09-13, the rule did
                    * not.** *SUPERSEDED, because it names a primitive this
                    * control no longer uses:* ***"Sized by the WRAPPER, not by a
                    * class on `Select`. The primitive's own `<select>` carries
                    * `w-full`, and a `w-auto` passed through `className` is the
                    * same CSS property — which of the two wins is decided by
                    * their order in the generated stylesheet, not by the order
                    * they appear here."***
                    *
                    * This is a `Combobox` now (owner's instruction: every
                    * non-table-column filter is searchable). `Combobox` **takes
                    * no `className` at all** — there is no prop to pass a width
                    * through — so it is sized entirely by its parent. The old
                    * warning described a race you could lose; the new one
                    * describes a prop that does not exist. **Either way this
                    * `<div>` is the only thing that sets the width, so deleting
                    * it lets the control size itself to its content.**
                    */}
                  <div className="w-56 max-sm:w-full">
                    <MultiCombobox
                      id={billingRegionFilterId}
                      values={billingRegionSelected}
                      onChange={(values) => setBillingRegionFilter(values.join(','))}
                      options={billingRegionOptions.map(([slug, label]) => ({
                        value: slug,
                        label,
                      }))}
                      placeholder={ALL_BILLING_REGIONS_LABEL}
                      searchPlaceholder="Search billing regions…"
                    />
                  </div>
                  {filtering && (
                    <Button variant="ghost" size="sm" onClick={() => setBillingRegionFilter('')}>
                      Clear
                    </Button>
                  )}
                </>
              )}

              {/*
                * The column manager, on the four WIDE shapes only — see
                * `MANAGED_SHAPES`. `Modal` has no header slot and is a shared
                * primitive this change does not touch, so it sits at the top of
                * the dialog's own content, in the same control row the one other
                * control on this screen already uses.
                */}
              {managed && (
                <div className="ml-auto">
                  <ColumnManager
                    layout={layout}
                    labelOf={columnLabelOf}
                    lockedKeys={LOCKED_MASTER_COLUMNS}
                    lockReasonOf={columnLockReason}
                  />
                </div>
              )}
            </div>
          )}

          {initialLoad ? (
            <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
              {/*
                * A SEPARATE `<Table>` mount from the loaded one below, so the
                * header row the offsets are measured from is unmounted and
                * replaced when the rows land. That is handled in
                * `useFrozenOffsets`, which tracks the observed ELEMENT and
                * re-measures when it changes — nothing here has to signal it.
                */}
              <Table className="min-w-[560px]">
                <MasterHead
                  shape={meta.shape}
                  columns={columns}
                  frozenSideOf={frozenSideOf}
                  frozenOffsets={frozenOffsets}
                  headRowRef={headRowRef}
                />
                <TBody>
                  {skeletonRows.map((index) => (
                    <TR key={index}>
                      {skeletonCells.map((cell) => (
                        <TD key={cell}>
                          <Skeleton className={cx('h-3', cell === 0 ? 'w-40' : 'w-16')} />
                        </TD>
                      ))}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={meta.icon}
              title={error ? 'Nothing to show' : `No ${meta.plural} configured`}
              description={
                error
                  ? 'This list couldn’t be loaded — see the message above.'
                  : canCreate
                    ? `Add the first one — pickers using this list have nothing to offer until you do.`
                    : `${denialReason(ability, 'create', masterModule) ?? ''} Pickers using this list have nothing to offer.`
              }
            />
          ) : visibleRows.length === 0 ? (
            /*
              * THE FILTER MATCHED NOTHING — a different fact from the list being
              * empty, and it must not borrow that copy. "No countries
              * configured" over 195 loaded countries would send somebody
              * looking for a broken fetch.
              *
              * Structurally unreachable today: the options are derived from the
              * rows, so every offered value has at least one row behind it. It
              * is here because "unreachable" is a property of this render, not
              * of the next one, and the branch it would otherwise fall into
              * states the opposite of the truth.
              */
            <EmptyState
              icon={meta.icon}
              title={`No ${meta.plural} in this billing region`}
              description="Clear the filter to see the whole list."
            />
          ) : (
            <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
              {/*
                * THE FLOOR MOVED OFF THE TABLE AND ONTO THIS WRAPPER, on the
                * four managed shapes only.
                *
                * It has to shrink as columns are hidden, so it is a NUMBER — and
                * ⛔ a number cannot be a class: Tailwind v4 scans source text, so
                * `min-w-[${n}px]` emits no rule and the table simply loses its
                * floor with nothing to see. `Table` takes `className` and
                * `children` and no `style`, and `src/components/ui/Table.tsx` is
                * out of scope for this change — so the number goes on a block
                * wrapper inside the scroller, which forces the same scroll width
                * while `Table`'s own `w-full` fills it.
                *
                * The three UNMANAGED shapes keep the literal they shipped with:
                * nothing can hide a column there, so there is nothing for a
                * computed floor to express.
                *
                * ⚠️ SUPERSEDED, the old per-shape ladder and its notes:
                * country 920 ("Seven columns since Billing region joined them. A
                * FLOOR, not a sum … Raised by the new column's `w-40`, and it now
                * has room — see `modalSizeFor`"), city 720, inclusion 780,
                * dealStage 820 ("Countries is wider now, and this floor no longer
                * fits inside an `lg` modal's 734px content box"). **Every one of
                * those totals survives unchanged as the sum of its column
                * shares** — see `MASTER_COLUMN_WIDTHS`, which is where the
                * reasoning now lives. `modalSizeFor` is untouched.
                *
                * ⚠️ SUPERSEDED, the spacer `<div>` that used to carry the number:
                * "`Table` takes `className` and `children` and no `style`, and
                * `src/components/ui/Table.tsx` is out of scope for this change —
                * so the number goes on a block wrapper inside the scroller."
                * `Table` takes a `style` now, so the floor sits on the element it
                * describes and the wrapper is gone. The `w-full` on the table
                * still fills anything wider.
                */}
              <Table
                className={tableMinWidth === undefined ? 'min-w-[600px]' : undefined}
                style={tableMinWidth === undefined ? undefined : { minWidth: tableMinWidth }}
              >
                <MasterHead
                  shape={meta.shape}
                  columns={columns}
                  frozenSideOf={frozenSideOf}
                  frozenOffsets={frozenOffsets}
                  headRowRef={headRowRef}
                />
                <TBody>
                  {visibleRows.map((row) => (
                    <TR key={row.id}>
                      {columns.map((key) =>
                        key === 'name' ? (
                          <TH
                            key={key}
                            scope="row"
                            className="min-w-[9rem]"
                            frozen={frozenSideOf(key)}
                            frozenOffset={frozenOffsets[key]}
                          >
                            {/*
                              * `break-words`, NOT `[overflow-wrap:anywhere]`.
                              *
                              * `anywhere` also drops the cell's intrinsic
                              * minimum width to a single character, so in a
                              * six-column table the name column collapses and
                              * every name breaks mid-word — "Ban / galo / re".
                              * `break-word` wraps an over-long word without
                              * telling the layout algorithm the column may be
                              * 1ch wide, and the min-width stops it being
                              * squeezed there in the first place.
                              */}
                            {/*
                              * The swatch never carries information on its
                              * own: the name is beside it, the hex has its own
                              * column, and `StageSwatch` is `aria-hidden`. It
                              * also renders NOTHING for an unusable or absent
                              * colour rather than a grey square, so the row
                              * never shows a colour the stage does not have.
                              */}
                            {meta.shape === 'dealStage' && (
                              <StageSwatch colour={row.colour} className="mr-2 align-middle" />
                            )}
                            <span className="break-words">{row.name}</span>
                            {meta.shape === 'country' && row.popular && (
                              <Badge tone="neutral" variant="outline" className="ml-2 align-middle">
                                Study destination
                              </Badge>
                            )}
                          </TH>
                        ) : key === 'actions' ? (
                          <TD
                            key={key}
                            frozen={frozenSideOf(key)}
                            frozenOffset={frozenOffsets[key]}
                          >
                            <div className="flex items-center justify-end gap-1">
                              {canUpdate && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconOnly
                                  aria-label={`Edit ${row.name}`}
                                  disabled={busy}
                                  leadingIcon={<PencilIcon />}
                                  onClick={() => {
                                    setEditing(row.seed);
                                    setFormOpen(true);
                                  }}
                                />
                              )}
                              {/*
                                * The obvious control, and deliberately a
                                * labelled button rather than an icon: this is
                                * the action almost everyone reaching for
                                * "remove" actually wants, so it should be the
                                * one that reads first. Not confirmed — it is
                                * reversible from this very row, and a
                                * confirmation would make the safe path feel
                                * heavier than the dangerous one.
                                */}
                              {canUpdate && (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  disabled={busy}
                                  loading={
                                    pending !== null &&
                                    pending.id === row.id &&
                                    (pending.kind === 'activate' || pending.kind === 'deactivate')
                                  }
                                  leadingIcon={row.isActive ? <ArchiveIcon /> : <RestoreIcon />}
                                  onClick={() => void toggleActive(row)}
                                >
                                  <span className="sr-only">
                                    {row.isActive
                                      ? `Deactivate ${row.name}`
                                      : `Reactivate ${row.name}`}
                                  </span>
                                  <span aria-hidden="true">
                                    {row.isActive ? 'Deactivate' : 'Reactivate'}
                                  </span>
                                </Button>
                              )}
                              {meta.deletable && canDelete && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconOnly
                                  aria-label={`Delete ${row.name} permanently`}
                                  disabled={busy}
                                  leadingIcon={<TrashIcon />}
                                  className="text-fg-subtle hover:text-danger"
                                  onClick={() => {
                                    setDeleteError(null);
                                    setDeleting(row);
                                  }}
                                />
                              )}
                            </div>
                          </TD>
                        ) : key === 'client' ||
                          key === 'pastClient' ||
                          key === 'partner' ||
                          key === 'defaultTier' ? (
                          <TD
                            key={key}
                            frozen={frozenSideOf(key)}
                            frozenOffset={frozenOffsets[key]}
                          >
                            {(() => {
                              const flag =
                                key === 'client'
                                  ? 'isClient'
                                  : key === 'pastClient'
                                    ? 'isPastClient'
                                    : key === 'partner'
                                      ? 'isPartner'
                                      : 'isDefault';
                              const value = row[flag];
                              /* `undefined` = an older API: no box to tick. */
                              if (value === undefined) {
                                return <span className="text-xs text-fg-subtle">{orDash(null)}</span>;
                              }
                              return (
                                <Checkbox
                                  checked={value}
                                  disabled={!canUpdate || busy}
                                  onChange={() => void toggleStageFlag(row, flag)}
                                  label={
                                    <span className="sr-only">
                                      {MASTER_COLUMN_LABELS[key]}: {row.name}
                                    </span>
                                  }
                                />
                              );
                            })()}
                          </TD>
                        ) : (
                          <MasterValueCell
                            key={key}
                            columnKey={key}
                            row={row}
                            frozen={frozenSideOf(key)}
                            frozenOffset={frozenOffsets[key]}
                          />
                        ),
                      )}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}

          {!meta.deletable && (
            /*
              * Countries have no delete control at all. Confirmed with the
              * backend: that endpoint has no in-use guard, so there is no 409 to
              * stop a country being removed while something still points at it,
              * and nobody has yet verified whether anything holds a foreign key
              * to `f_countries`. `api.ts` has no `deleteCountry` either — a
              * delete we cannot make safe should not exist in the client.
              */
            <p className="m-0 text-xs text-fg-subtle">
              Countries cannot be deleted — there is no in-use check to make it safe. Deactivate
              instead.
            </p>
          )}

          {/*
            * ⚠️ THE ONE HAZARD ON THESE TWO LISTS THAT NO ERROR EVER REPORTS.
            *
            * `POST /quotes/{id}/convert` resolves the invoice status `Draft`
            * and the quote stage `Accepted` BY NAME, inside its transaction,
            * and DEGRADES SILENTLY when the row is missing: the invoice arrives
            * with `status: null`, or the quote simply keeps the stage it had.
            * There is no 409, no 422 and nothing in the response to notice.
            *
            * So renaming or retiring either row is a change whose consequence
            * is invisible — which is exactly why it is said HERE, beside the
            * rename control, rather than left to be discovered by an accountant
            * wondering why a converted quote never moved. Both names are shown
            * quoted and exact, because a trailing space or a different case is
            * a different row as far as that lookup is concerned.
            */}
          {(meta.kind === 'quoteStages' || meta.kind === 'invoiceStatuses') && (
            <p className="m-0 text-xs text-fg-subtle">
              Convert looks up{' '}
              <strong>{meta.kind === 'quoteStages' ? '“Accepted”' : '“Draft”'}</strong>{' '}
              <strong>by name</strong>. Rename it and that step is skipped silently —{' '}
              {meta.kind === 'quoteStages'
                ? 'the quote keeps its stage.'
                : 'the invoice arrives with no status.'}
            </p>
          )}

          {meta.shape === 'picker' && (
            <p className="m-0 text-xs text-fg-subtle">
              Deleting is refused while anything still uses the value.
            </p>
          )}

          {meta.shape === 'city' && (
            <p className="m-0 text-xs text-fg-subtle">
              Deleting is refused while a project or line still names the city.
            </p>
          )}
        </div>
      </Modal>

      <MasterFormModal
        open={formOpen}
        kind={meta.kind}
        record={editing}
        onClose={() => setFormOpen(false)}
        onSaved={onReload}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          setDeleting(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={`Delete “${deleting?.name ?? ''}” permanently?`}
        confirmLabel="Delete permanently"
        loading={pending?.kind === 'delete'}
        error={deleteError}
        description={
          <>
            <p className="m-0">
              The {meta.singular} <strong>{deleting?.name}</strong> will be deleted. This can't be
              undone.
            </p>
            <p className="mt-2 mb-0">
              If it has been used anywhere, deactivate it instead.
            </p>
          </>
        }
      />
    </>
  );
}

/** One place to turn a write failure into something a person can act on. */
function describeWriteError(
  caught: unknown,
  singular: string,
  verb: string,
  reason: string | null,
): string {
  if (caught instanceof ApiError) {
    if (caught.status === 403) {
      return `${reason ?? `The server refused to ${verb} that ${singular}.`} You can view this list but not change it.`;
    }
    if (caught.status === 404) {
      return `That ${singular} no longer exists — close and reopen this list.`;
    }
    return caught.message;
  }
  return `Couldn’t ${verb} that ${singular}. Check your connection.`;
}

// --- the cycles / intakes modal -----------------------------------------

/**
 * `modifiedBy === null` is the ONLY reliable "never edited" signal on these two
 * resources. `modifiedAt` is not: on an intake it is NOT NULL and is stamped at
 * insert, so a brand-new row already carries one.
 */
function PeriodEditedCell({ row }: { row: PeriodRow }) {
  if (row.modifiedBy === null) {
    return <span className="text-xs text-fg-subtle">Never edited</span>;
  }
  return (
    <span className="text-xs text-fg-muted">
      {row.modifiedAt ? formatDateOnly(row.modifiedAt) : 'Edited'}
      <span className="text-fg-subtle"> by {row.modifiedBy}</span>
    </span>
  );
}

/**
 * The same one-list-per-kind model the masters use. ⚠️ **SUPERSEDED:
 * `periodColumnCount(kind, showActions)`** — see `MASTER_COLUMNS` for why a
 * hand-kept count beside two independent lists of conditions is a promise only a
 * person can keep.
 */
type PeriodColumnKey = 'name' | 'cycle' | 'starts' | 'ends' | 'created' | 'edited' | 'actions';

const PERIOD_COLUMNS: Record<PeriodKind, readonly PeriodColumnKey[]> = {
  cycles: ['name', 'starts', 'ends', 'created', 'edited'],
  intakes: ['name', 'cycle', 'starts', 'ends', 'created', 'edited'],
};

/** Same two as the masters, for the same two reasons — see `LOCKED_MASTER_COLUMNS`. */
const LOCKED_PERIOD_COLUMNS: readonly PeriodColumnKey[] = ['name', 'actions'];

const PERIOD_COLUMN_LABELS: Record<Exclude<PeriodColumnKey, 'name'>, string> = {
  cycle: 'Cycle',
  starts: 'Starts',
  ends: 'Ends',
  created: 'Created',
  edited: 'Last edited',
  actions: 'Actions',
};

/*
 * ⚠️ **`Cycle` IS BOTH A LABEL AND A KEY HERE AND THEY ARE DIFFERENT COLUMNS.**
 * On the cycles list the NAME column reads "Cycle"; on the intakes list the name
 * column reads "Intake" and a separate `cycle` column reads "Cycle". A manager
 * row labelled "Cycle" on the intakes list is therefore the PARENT, never the
 * row's own name — which is also why `name` is locked and never offered.
 */
function periodColumnLabel(kind: PeriodKind, key: PeriodColumnKey): string {
  if (key !== 'name') return PERIOD_COLUMN_LABELS[key];
  return kind === 'intakes' ? 'Intake' : 'Cycle';
}

/* Literal strings — see `MASTER_TH_CLASS`. */
const PERIOD_TH_CLASS: Partial<Record<PeriodColumnKey, string>> = {
  cycle: 'w-40',
  starts: 'w-28',
  ends: 'w-28',
  created: 'w-32',
  edited: 'w-44',
  actions: 'w-24',
};

/**
 * 🔴 **INTAKES ONLY.** Six data columns is the wide one; cycles has five and
 * gets no manager, on the same reasoning as the four-column masters.
 */
const INTAKE_COLUMN_WIDTHS: Record<PeriodColumnKey, number> = {
  name: 156,
  cycle: 148,
  starts: 100,
  ends: 100,
  created: 116,
  edited: 144,
  actions: 96,
};

/** Shares of the shipped 860 — see `MASTER_COLUMN_WIDTHS` for why shares. */
function periodTableMinWidth(
  kind: PeriodKind,
  columns: readonly PeriodColumnKey[],
): number | undefined {
  if (kind !== 'intakes') return undefined;
  return columns.reduce((total, key) => total + INTAKE_COLUMN_WIDTHS[key], 0);
}

function PeriodHead({
  meta,
  columns,
  frozenSideOf,
  frozenOffsets,
  headRowRef,
}: {
  meta: PeriodMeta;
  columns: readonly PeriodColumnKey[];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
  /* Measured — see `MasterHead`. */
  headRowRef: RefObject<HTMLTableRowElement | null>;
}) {
  return (
    <THead>
      <TR ref={headRowRef}>
        {columns.map((key) => (
          <TH
            key={key}
            frozen={frozenSideOf(key)}
            frozenOffset={frozenOffsets[key]}
            className={PERIOD_TH_CLASS[key]}
          >
            {key === 'actions' ? (
              <span className="sr-only">{periodColumnLabel(meta.kind, key)}</span>
            ) : (
              periodColumnLabel(meta.kind, key)
            )}
          </TH>
        ))}
      </TR>
    </THead>
  );
}

/**
 * One value cell of the periods table. No `default`, so a column added to the
 * key list and not here fails at `tsc` rather than rendering blank.
 */
function PeriodValueCell({
  columnKey,
  row,
  frozen,
  frozenOffset,
}: {
  columnKey: Exclude<PeriodColumnKey, 'name' | 'actions'>;
  row: PeriodRow;
  frozen: FrozenSide | undefined;
  frozenOffset: number | undefined;
}) {
  const edge = { frozen, frozenOffset };

  switch (columnKey) {
    case 'cycle':
      return (
        <TD {...edge} className="text-xs text-fg-muted">
          <span className="break-words">{row.cycleLabel}</span>
        </TD>
      );

    /* Formatted from the plain 'YYYY-MM-DD' string. No Date is constructed
       anywhere on this path — that is how an end-of-cycle date loses a day west
       of Greenwich. */
    case 'starts':
      return (
        <TD {...edge} className="text-xs whitespace-nowrap text-fg-muted tabular">
          {formatDateOnly(row.startDate)}
        </TD>
      );

    case 'ends':
      return (
        <TD {...edge} className="text-xs whitespace-nowrap text-fg-muted tabular">
          {formatDateOnly(row.endDate)}
        </TD>
      );

    case 'created':
      return (
        <TD {...edge} className="text-xs whitespace-nowrap text-fg-muted">
          {formatDateOnly(row.createdAt)}
          <span className="block text-2xs text-fg-subtle">by {row.createdBy}</span>
        </TD>
      );

    case 'edited':
      return (
        <TD {...edge}>
          <PeriodEditedCell row={row} />
        </TD>
      );
  }
}

/**
 * The list behind the Cycles or Intakes card.
 *
 * Shaped like `MasterListModal` on purpose so all eight lists read as one
 * system, but the behaviour underneath is deliberately different in three ways,
 * each of which is a real API difference rather than a styling choice:
 *
 *  1. NO DEACTIVATE. There is no `isActive` column on either table, so there is
 *     no reversible retire to offer and no copy here may imply one.
 *  2. DELETE IS THE ONLY REMOVAL, and its guarantees differ: a cycle with
 *     intakes is a 409 (surfaced with a link to exactly those intakes, because
 *     the refusal cannot be acted on from here), while an intake delete has no
 *     in-use guard at all.
 *  3. NO SEARCH AND NO SORT exist server-side, so neither is offered. Rows
 *     render in the order the API returned them, which is `id DESC`.
 */
function PeriodListModal({
  meta,
  open,
  onClose,
  rows,
  loading,
  error,
  ability,
  onReload,
  cycles,
  cyclesLoading,
  cyclesFailed,
}: {
  meta: PeriodMeta;
  open: boolean;
  onClose: () => void;
  rows: PeriodRow[];
  loading: boolean;
  error: string | null;
  ability: Ability;
  onReload: () => void;
  /** The loaded cycle list, for the intake form's parent picker. */
  cycles: Cycle[];
  cyclesLoading: boolean;
  /**
   * The cycle read FAILED, as against having returned nothing. Both arrive as
   * an empty `cycles`, and the intake form's cycle picker is required — so
   * without this it shows an empty required dropdown that reads as "no cycles
   * exist yet" and a Save button with no stated reason for being disabled.
   */
  cyclesFailed: boolean;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PeriodRow | null>(null);
  const [deleting, setDeleting] = useState<PeriodRow | null>(null);
  const [deleteError, setDeleteError] = useState<ReactNode>(null);
  const [busy, setBusy] = useState(false);

  /*
   * Cycles and intakes are admin-only to write server-side too, regardless of
   * what `ability` grants — same reasoning as the masters' `canWrite` below.
   */
  const canCreate = ability.canCreate(meta.module);
  const canUpdate = ability.canUpdate(meta.module);
  const canDelete = ability.canDelete(meta.module);
  const updateDenial = denialReason(ability, 'update', meta.module);

  const showActions = canUpdate || canDelete;

  const declaredColumns = useMemo<readonly PeriodColumnKey[]>(
    () =>
      showActions ? [...PERIOD_COLUMNS[meta.kind], 'actions' as const] : PERIOD_COLUMNS[meta.kind],
    [meta.kind, showActions],
  );

  /* Intakes is the six-column list; cycles has five and gets no manager. */
  const managed = meta.kind === 'intakes';

  /*
   * Called for both kinds and read for one — the same shape, and the same
   * reasoning, as `MasterListModal`'s call. A hook cannot be conditional, and
   * the cycles list must not APPLY a layout it offers no control to undo.
   */
  const layout = useColumnLayout(`catalog.${meta.kind}`, declaredColumns, LOCKED_PERIOD_COLUMNS);

  const columns = (managed ? layout.visibleKeys : declaredColumns) as readonly PeriodColumnKey[];

  /* Destructured — see `MasterListModal`. */
  const { frozenSideOf: layoutFrozenSideOf } = layout;
  const frozenSideOf = useCallback(
    (key: string) => (managed ? layoutFrozenSideOf(key) : undefined),
    [managed, layoutFrozenSideOf],
  );

  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, columns, frozenSideOf);

  const columnLabelOf = useCallback(
    (key: string) => periodColumnLabel(meta.kind, key as PeriodColumnKey),
    [meta.kind],
  );

  /** Why each locked column is locked — see `MasterListModal`'s copy. The name
   *  cell is this row's `<th scope="row">` on both lists, and on intakes it is
   *  the INTAKE rather than the `cycle` column beside it. */
  const columnLockReason = useCallback(
    (key: string): string | undefined => {
      if (key === 'name') {
        return `The row’s own heading — hidden, every row here becomes dates belonging to no ${meta.kind === 'intakes' ? 'intake' : 'cycle'}`;
      }
      if (key === 'actions') {
        return 'Edit and delete live here and nowhere else on this screen';
      }
      return undefined;
    },
    [meta.kind],
  );

  /* The Escape workaround that stood here is gone — `ColumnManager` binds in the
     capture phase now. The whole chain is written out at `MasterListModal`'s
     copy, which is where the reasoning lives. */

  const initialLoad = loading && rows.length === 0;
  const skeletonRows = Array.from({ length: SKELETON_ROWS }, (_, index) => index);
  /* The LIVE count — see `MasterListModal`. */
  const skeletonCells = Array.from({ length: columns.length }, (_, index) => index);
  const tableMinWidth = periodTableMinWidth(meta.kind, columns);

  function handleClose() {
    setDeleting(null);
    setDeleteError(null);
    onClose();
  }

  async function confirmDelete() {
    if (!deleting || busy) return;
    setBusy(true);
    setDeleteError(null);
    try {
      if (meta.kind === 'cycles') await api.deleteCycle(deleting.id);
      else await api.deleteIntake(deleting.id);
      setDeleting(null);
      onReload();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        /*
         * Cycles only, and the backend refuses rather than cascading. Its
         * message names the count ("Cycle 8 still has 4 intake(s)"), so it is
         * shown as-is and paired with the one route onward — the user cannot
         * act on the refusal from inside this list.
         */
        setDeleteError(
          <>
            {caught.message}{' '}
            <Link
              to={`/intakes?cycleId=${deleting.id}`}
              className="font-medium underline underline-offset-2"
            >
              Review its intakes
            </Link>{' '}
            first — nothing was deleted.
          </>,
        );
      } else if (caught instanceof ApiError && caught.status === 404) {
        // Already gone: that is the outcome the user asked for, so it is not an
        // error. Reload so the row disappears.
        setDeleting(null);
        onReload();
      } else {
        setDeleteError(
          describeWriteError(
            caught,
            meta.singular,
            'delete',
            denialReason(ability, 'delete', meta.module),
          ),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  const fullPageLink = (
    <TextLink
      to={meta.path}
      className="inline-flex items-center gap-1 self-center text-xs font-medium sm:mr-auto"
    >
      {meta.pageLabel}
      <ChevronIcon direction="right" />
    </TextLink>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={meta.title}
        size="lg"
        footer={
          <>
            {fullPageLink}
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            {canCreate && (
              <Button
                variant="primary"
                leadingIcon={<PlusIcon />}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                New {meta.singular}
              </Button>
            )}
          </>
        }
      >
        <div className="grid gap-3">
          {error && (
            <ErrorBlock>
              Couldn’t load {meta.plural}. {error}
            </ErrorBlock>
          )}

          {updateDenial && (
            <p className="m-0 text-xs text-fg-subtle">{updateDenial} You can read it here.</p>
          )}

          {/*
            * Intakes only — see `MANAGED_SHAPES` for why the narrow list gets
            * nothing rather than a disabled control. `Modal` has no header slot
            * and is not touched by this change, so the trigger sits at the top of
            * the dialog's own content.
            */}
          {managed && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="ml-auto">
                <ColumnManager
                  layout={layout}
                  labelOf={columnLabelOf}
                  lockedKeys={LOCKED_PERIOD_COLUMNS}
                  lockReasonOf={columnLockReason}
                />
              </div>
            </div>
          )}

          {initialLoad ? (
            <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
              {/* A separate `<Table>` mount from the loaded one — the header row
                  swap that costs is handled in `useFrozenOffsets`. */}
              <Table className={meta.kind === 'intakes' ? 'min-w-[860px]' : 'min-w-[700px]'}>
                <PeriodHead
                  meta={meta}
                  columns={columns}
                  frozenSideOf={frozenSideOf}
                  frozenOffsets={frozenOffsets}
                  headRowRef={headRowRef}
                />
                <TBody>
                  {skeletonRows.map((index) => (
                    <TR key={index}>
                      {skeletonCells.map((cell) => (
                        <TD key={cell}>
                          <Skeleton className={cx('h-3', cell === 0 ? 'w-40' : 'w-20')} />
                        </TD>
                      ))}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={meta.icon}
              title={error ? 'Nothing to show' : `No ${meta.plural} yet`}
              description={
                error
                  ? 'This list couldn’t be loaded — see the message above.'
                  : meta.kind === 'cycles'
                    ? canCreate
                      ? 'Add the first business year for intakes to hang off.'
                      : `${denialReason(ability, 'create', 'cycles') ?? ''} Intakes need a cycle.`
                    : cycles.length === 0 && !cyclesLoading
                      ? 'No cycles yet — add one of those first.'
                      : canCreate
                        ? 'Add the first intake window inside a cycle.'
                        : `${denialReason(ability, 'create', 'intakes') ?? ''} Intakes are the windows inside a cycle.`
              }
            />
          ) : (
            <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
              {/*
                * The floor is a NUMBER on intakes so it shrinks with the hidden
                * columns, and it sits on the table's own `style` — ⛔ never a
                * computed `min-w-[…]` class, which Tailwind's source scan cannot
                * see and which therefore emits nothing at all. Cycles keeps the
                * literal it shipped with.
                *
                * ⚠️ SUPERSEDED, the spacer `<div>` this used to need: "`Table`
                * takes no `style` … so the number goes on this wrapper."
                */}
              <Table
                className={tableMinWidth === undefined ? 'min-w-[700px]' : undefined}
                style={tableMinWidth === undefined ? undefined : { minWidth: tableMinWidth }}
              >
                <PeriodHead
                  meta={meta}
                  columns={columns}
                  frozenSideOf={frozenSideOf}
                  frozenOffsets={frozenOffsets}
                  headRowRef={headRowRef}
                />
                <TBody>
                  {rows.map((row) => (
                    <TR key={row.id}>
                      {columns.map((key) =>
                        key === 'name' ? (
                          <TH
                            key={key}
                            scope="row"
                            className="min-w-[9rem]"
                            frozen={frozenSideOf(key)}
                            frozenOffset={frozenOffsets[key]}
                          >
                            {/* `break-words`, not `[overflow-wrap:anywhere]` — same
                                column-collapse reasoning as MasterListModal's name cell. */}
                            <span className="break-words">{row.name}</span>
                          </TH>
                        ) : key === 'actions' ? (
                          <TD
                            key={key}
                            frozen={frozenSideOf(key)}
                            frozenOffset={frozenOffsets[key]}
                          >
                            <div className="flex items-center justify-end gap-1">
                              {canUpdate && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconOnly
                                  aria-label={`Edit ${row.name}`}
                                  title={`Edit ${row.name}`}
                                  disabled={busy}
                                  leadingIcon={<PencilIcon />}
                                  onClick={() => {
                                    setEditing(row);
                                    setFormOpen(true);
                                  }}
                                />
                              )}
                              {canDelete && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconOnly
                                  aria-label={`Delete ${row.name} permanently`}
                                  title={`Delete ${row.name} permanently`}
                                  disabled={busy}
                                  leadingIcon={<TrashIcon />}
                                  className="text-fg-subtle hover:text-danger"
                                  onClick={() => {
                                    setDeleteError(null);
                                    setDeleting(row);
                                  }}
                                />
                              )}
                            </div>
                          </TD>
                        ) : (
                          <PeriodValueCell
                            key={key}
                            columnKey={key}
                            row={row}
                            frozen={frozenSideOf(key)}
                            frozenOffset={frozenOffsets[key]}
                          />
                        ),
                      )}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </div>
      </Modal>

      {meta.kind === 'cycles' ? (
        <CycleFormModal
          open={formOpen}
          cycle={cycleSeed(editing)}
          onClose={() => setFormOpen(false)}
          onSaved={onReload}
        />
      ) : (
        <IntakeFormModal
          open={formOpen}
          intake={intakeSeed(editing)}
          cycles={cycles}
          cyclesLoading={cyclesLoading}
          cyclesFailed={cyclesFailed}
          onClose={() => setFormOpen(false)}
          onSaved={onReload}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          setDeleting(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={`Delete “${deleting?.name ?? ''}” permanently?`}
        confirmLabel={`Delete ${meta.singular}`}
        loading={busy}
        error={deleteError}
        description={
          meta.kind === 'cycles' ? (
            <>
              <p className="m-0">
                The cycle <strong>{deleting?.name}</strong> will be deleted. This can't be undone,
                and a cycle can't be retired instead.
              </p>
              <p className="mt-2 mb-0">A cycle that still has intakes can't be deleted.</p>
            </>
          ) : (
            <>
              <p className="m-0">
                The intake <strong>{deleting?.name}</strong> will be deleted. This can't be undone,
                and an intake can't be retired instead.
              </p>
              <p className="mt-2 mb-0">
                An intake a project still uses can't be deleted.
              </p>
            </>
          )
        }
      />
    </>
  );
}

// --- page ---------------------------------------------------------------

/** Layout manager blocks — labels are the bands' own headings; ids are stored per user. */
const CATALOG_LAYOUT = [
  { id: 'managed-lists', label: 'Managed lists' },
  { id: 'records', label: 'Records without a sidebar row' },
  { id: 'company-mapper', label: 'Company mapper' },
  { id: 'account-lead-import', label: 'Account lead history' },
] as const;

export function CatalogPage() {
  const { ability } = useAuth();

  /*
   * Reads on the masters are open to every authenticated account, so this page
   * is never hidden. Writes are admin-only, and that is enforced server-side
   * however generous the permission set looks — the seeded `advanced-user` set
   * claims `reference-data` writes the routers refuse. `ability` is the single
   * predicate for it, so there is one call site to change the day the backend
   * switches enforcement onto the permission model.
   */
  /*
   * 2026-10-02: each master card asks its own catalogue module — see
   * `MASTER_MODULE`. *SUPERSEDED: one `reference-data` answer for every card.*
   */
  const canWriteMaster = (kind: MasterKind): boolean =>
    ability.canCreate(MASTER_MODULE[kind]) || ability.canUpdate(MASTER_MODULE[kind]);

  /*
   * The Company mapper opens in a large modal (owner 2026-10-25).
   * `/catalog#company-mapper` (the Product Insights "Open Company mapper" link)
   * arrives with it open; closing drops the hash so a reload does not reopen it.
   */
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [mapperOpen, setMapperOpen] = useState(() => hash === '#company-mapper');
  const closeMapper = () => {
    setMapperOpen(false);
    if (hash === '#company-mapper') navigate({ hash: '' }, { replace: true });
  };

  /*
   * Account lead history import (owner 2026-10-25): the same large-modal
   * pattern as the Company mapper, deep-linked by `/catalog#account-lead-import`.
   */
  const canImportLeadHistory = ability.canUpdate('account-lead-history');
  const [leadImportOpen, setLeadImportOpen] = useState(() => hash === '#account-lead-import');
  const closeLeadImport = () => {
    setLeadImportOpen(false);
    if (hash === '#account-lead-import') navigate({ hash: '' }, { replace: true });
  };

  const regions = useMasterList(loadRegions, 'regions of focus', REFERENCE_QUERY);
  const bookingStatuses = useMasterList(loadBookingStatuses, 'booking statuses', REFERENCE_QUERY);
  const travelPackages = useMasterList(loadTravelPackages, 'travel packages', REFERENCE_QUERY);
  const adsPlans = useMasterList(loadAdsPlans, 'ads plans', REFERENCE_QUERY);
  const currencies = useMasterList(loadCurrencies, 'currencies', WIDE_QUERY);
  const countries = useMasterList(loadCountries, 'countries', WIDE_QUERY);
  const cities = useMasterList(loadCities, 'cities', CITY_QUERY);
  /*
   * Campaign inclusions share the cities ceiling — 1..200, default 50 — because
   * they come from the same backend module. `CITY_QUERY` is reused rather than
   * cloned: two constants holding the same number is how one of them drifts.
   */
  const campaignInclusions = useMasterList(
    loadCampaignInclusions,
    'campaign inclusions',
    CITY_QUERY,
  );

  /*
   * Deal stages come from the deals module, not the reference-data one, so they
   * take their own query — `perPage` 100, which is that endpoint's ceiling.
   */
  const dealStages = useMasterList(loadDealStages, 'deal stages', PICKER_QUERY);

  /*
   * The remaining reference masters, on the masters' own query — same endpoint
   * family as regions and booking statuses, so the same 200 ceiling and the
   * same absent `isActive`.
   */
  const companyStages = useMasterList(loadCompanyStages, 'company stages', REFERENCE_QUERY);
  const companySources = useMasterList(loadCompanySources, 'company sources', REFERENCE_QUERY);
  const companyTypes = useMasterList(loadCompanyTypes, 'company types', REFERENCE_QUERY);
  const quoteStages = useMasterList(loadQuoteStages, 'quote stages', REFERENCE_QUERY);
  const invoiceStatuses = useMasterList(loadInvoiceStatuses, 'invoice statuses', REFERENCE_QUERY);
  const dealLostReasons = useMasterList(
    loadDealLostReasons,
    'deal lost reasons',
    REFERENCE_QUERY,
  );
  const contactTitles = useMasterList(loadContactTitles, 'contact titles', REFERENCE_QUERY);
  const priceTiers = useMasterList(loadPriceTiers, 'price tiers', REFERENCE_QUERY);

  /*
   * The two remaining deal pickers, on `PICKER_QUERY` with deal stages — same
   * module, same 100 ceiling, same `active` filter deliberately omitted.
   */
  const dealTypes = useMasterList(loadDealTypes, 'deal types', PICKER_QUERY);
  const leadSources = useMasterList(loadLeadSources, 'lead sources', PICKER_QUERY);

  /*
   * Cycles and intakes are loaded in full now rather than counted, because the
   * card opens a list rather than navigating away. The cycle list does double
   * duty: the intake read carries only `cycleId`, so the Cycle column and the
   * intake form's parent picker are both joined against this one fetch.
   */
  const cycles = useMasterList(loadCycles, 'cycles', ENTITY_QUERY);
  const intakes = useMasterList(loadIntakes, 'intakes', ENTITY_QUERY);

  /*
   * Contacts, for the count on its card and nothing else. It is loaded through
   * the same hook as everything else on this page so that one endpoint being
   * unavailable leaves the other eight cards rendered, and so the card gets the
   * page's usual loading / error / loaded treatment for free.
   *
   * There are ZERO contacts in production today and that is the intended state,
   * so "0 contacts" is the normal reading of this card rather than a fault. The
   * card carries no copy at all now — which is the right shape for that, since
   * any sentence here would have had to describe an empty list without implying
   * rows are on their way.
   */
  const contacts = useMasterList(loadContacts, 'contacts', COUNT_QUERY);

  /**
   * The currency master indexed by id, so a country's `currencyId` can be
   * resolved locally. The list is already loaded for its own card, and asking
   * for `perPage: 250` means it is the whole master rather than a first page.
   *
   * ⚠️ **It carries the FETCH'S STATE with it, and the previous version did
   * not.** `currencies.result?.data ?? []` produced an empty index for a request
   * still in flight and an empty index for a master with no rows, so every
   * lookup missed identically in both — and a miss rendered as the em dash that
   * means "this country has no currency". That is the bug: the Countries table
   * showed `—` for Afghanistan on one read and `AFN` on the next.
   *
   * Passing `error` in is what separates "not yet" from "we asked and could
   * not"; `?? []` had no way to express either.
   */
  const currencyJoin = useMemo<MasterJoin<Currency>>(
    () => masterJoin(currencies.result, currencies.error, (row) => row.id),
    [currencies.result, currencies.error],
  );

  /**
   * The billing-region master indexed by slug, for the Countries table's
   * Billing region column. Same join shape as `currencyJoin` above — fetch the
   * master once, resolve locally — because the country read carries the SLUG
   * and no display name.
   */
  const billingRegionNames = useBillingRegionNames();

  const cycleList = useMemo(() => cycles.result?.data ?? [], [cycles.result]);

  /** The cycle list indexed by id, for the intake table's Cycle column. */
  const cycleIndex = useMemo(() => {
    const index = new Map<number, Cycle>();
    for (const row of cycleList) index.set(row.id, row);
    return index;
  }, [cycleList]);

  const periodRows: Record<PeriodKind, PeriodRow[]> = useMemo(
    () => ({
      cycles: cycleList.map(cycleRow),
      intakes: (intakes.result?.data ?? []).map((row) => intakeRow(row, cycleIndex)),
    }),
    [cycleList, intakes.result, cycleIndex],
  );

  const [openKind, setOpenKind] = useState<MasterKind | null>(null);
  const [openPeriod, setOpenPeriod] = useState<PeriodKind | null>(null);

  const states: Record<MasterKind, { rows: Row[]; state: ListState<unknown> }> = useMemo(() => {
    const reference = (state: ListState<ReferenceRecord>) => ({
      rows: (state.result?.data ?? []).map(referenceRow),
      state: state as ListState<unknown>,
    });
    return {
      regions: reference(regions),
      bookingStatuses: reference(bookingStatuses),
      travelPackages: reference(travelPackages),
      adsPlans: reference(adsPlans),
      currencies: {
        rows: (currencies.result?.data ?? []).map(currencyRow),
        state: currencies as ListState<unknown>,
      },
      countries: {
        rows: (countries.result?.data ?? []).map((row) =>
          countryRow(row, currencyJoin, billingRegionNames),
        ),
        state: countries as ListState<unknown>,
      },
      /*
       * No index to join against, unlike countries: a city arrives with its
       * country and its region already resolved to `{id, name}`. That is also
       * the safeguard — there is no second list in scope here to accidentally
       * resolve a region out of.
       */
      cities: {
        rows: (cities.result?.data ?? []).map(cityRow),
        state: cities as ListState<unknown>,
      },
      campaignInclusions: {
        rows: (campaignInclusions.result?.data ?? []).map(inclusionRow),
        state: campaignInclusions as ListState<unknown>,
      },
      /*
       * Mapped in the order received and never re-sorted here. `sort_order ASC,
       * id ASC` is what the endpoint returns and the only thing carrying
       * pipeline order — see `dealStageRow`.
       */
      dealStages: {
        rows: (dealStages.result?.data ?? []).map(dealStageRow),
        state: dealStages as ListState<unknown>,
      },
      /*
       * The remaining masters go through the same `reference` helper as the
       * four picklists above — same row shape, same mapper, nothing to special
       * case. That is the payoff of the backend serving every one of them from
       * one parameterised module, and it is why adding a master here is a
       * `MASTERS` entry and a loader rather than a screen.
       */
      companyStages: reference(companyStages),
      companySources: reference(companySources),
      companyTypes: reference(companyTypes),
      quoteStages: reference(quoteStages),
      invoiceStatuses: reference(invoiceStatuses),
      dealLostReasons: reference(dealLostReasons),
      contactTitles: reference(contactTitles),
      priceTiers: reference(priceTiers),
      /*
       * The two deal pickers, mapped in the order received. `sort_order ASC,
       * id ASC` — by ID on a tie, unlike the masters' name — so re-sorting here
       * would make this screen disagree with the dropdowns it documents.
       */
      dealTypes: {
        rows: (dealTypes.result?.data ?? []).map(pickerRow),
        state: dealTypes as ListState<unknown>,
      },
      leadSources: {
        rows: (leadSources.result?.data ?? []).map(pickerRow),
        state: leadSources as ListState<unknown>,
      },
    };
  }, [
    regions,
    bookingStatuses,
    travelPackages,
    adsPlans,
    currencies,
    countries,
    cities,
    campaignInclusions,
    dealStages,
    companyStages,
    companySources,
    companyTypes,
    quoteStages,
    invoiceStatuses,
    dealLostReasons,
    contactTitles,
    priceTiers,
    dealTypes,
    leadSources,
    currencyJoin,
    billingRegionNames,
  ]);

  const openMeta = MASTERS.find((entry) => entry.kind === openKind) ?? null;
  const openEntry = openKind ? states[openKind] : null;

  const openPeriodMeta = PERIODS.find((entry) => entry.kind === openPeriod) ?? null;
  const openPeriodState = openPeriod === 'intakes' ? intakes : cycles;
  const cyclesTruncated = (cycles.result?.meta.totalPages ?? 1) > 1;

  const layout = usePageLayout('catalog', CATALOG_LAYOUT);

  return (
    <>
      {/*
       * Eyebrow is "Index", not "Reference data" — contacts is neither
       * reference data nor a managed list, so that label would describe less
       * than the page holds.
       */}
      <PageHeader
        eyebrow="Index"
        title="Catalog"
        actions={<LayoutToolbar layout={layout} />}
      />

      {/*
        * Layout manager (owner 2026-10-20): the two bands are movable blocks,
        * per person. `gap-y-8` keeps today's `mt-8` between them; the defaults
        * are today's order, both full width. Block ids are stored per user —
        * never rename one.
        */}
      <LayoutGrid
        layout={layout}
        className="gap-y-8"
        render={{
          'managed-lists': () => (
      <section aria-labelledby="catalog-managed-lists">
        <SectionHeading
          id="catalog-managed-lists"
          title="Managed lists"
        />

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {MASTERS.map((meta) => {
            const entry = states[meta.kind];
            return (
              <MasterCard
                key={meta.kind}
                meta={meta}
                rows={entry.rows}
                total={entry.state.result?.meta.total ?? entry.rows.length}
                loading={entry.state.loading}
                error={entry.state.error}
                canWrite={canWriteMaster(meta.kind)}
                onOpen={() => setOpenKind(meta.kind)}
              />
            );
          })}

          {PERIODS.map((meta) => {
            const state = meta.kind === 'intakes' ? intakes : cycles;
            return (
              <PeriodCard
                key={meta.kind}
                meta={meta}
                total={state.result?.meta.total ?? null}
                loading={state.loading}
                error={state.error}
                canWrite={ability.canCreate(meta.module) || ability.canUpdate(meta.module)}
                onOpen={() => setOpenPeriod(meta.kind)}
              />
            );
          })}

          {/*
           * Target periods, beside Cycles and Intakes because a period is the
           * third named window of time on this screen — and one of its four
           * kinds literally names a cycle.
           *
           * It owns its own fetch, list modal and form rather than joining
           * `MASTERS` or `PERIODS`: its row is neither a `ReferenceRecord` (no
           * `sortOrder`, two dates, a kind, a cycle) nor a cycle/intake (it DOES
           * have `isActive`, and retiring is the recommended removal). Forcing it
           * into either registry would mean widening `Row` or `PeriodRow` with
           * columns four other lists have no value for.
           *
           * The cycle list is passed in rather than fetched again — this page has
           * already loaded it for its own card and for the intake form's parent
           * picker, and a third request for the same rows is a cost with no
           * reader.
           */}
          <TargetPeriodsCard
            ability={ability}
            cycles={cycleList}
            cyclesLoading={cycles.loading}
            cyclesFailed={cycles.error !== null}
            cyclesTruncated={cyclesTruncated}
          />

          {/*
           * Group targets, immediately beside the periods they are set over —
           * every one of them names a window from the card above, and the two
           * empty states point at each other.
           *
           * Self-contained for the same reasons `TargetPeriodsCard` is, plus one
           * of its own: its row is a TARGET, so it is neither a
           * `ReferenceRecord` nor a `PeriodRow`, and every `MASTERS` entry is
           * handed a New button and a delete by construction. Folding it in
           * would grant verbs from the shape of the registry rather than from
           * what `/targets` actually offers.
           *
           * ⚠️ It takes `ability` and nothing else — deliberately NOT the cycle
           * list. A target names a target PERIOD, and a period may name a cycle;
           * passing cycles here would invite the reading that a target names one
           * directly.
           */}
          <GroupTargetsCard ability={ability} />

          {/*
           * Contact groups and billing region defaults — both live server-side
           * for some time with no screen at all, so until now the only way to
           * edit either was raw SQL.
           *
           * Self-contained cards for the same reason `TargetPeriodsCard` above
           * is one, and the backend gives the same reason for keeping both out
           * of its own reference registry: a registry master is exactly
           * `{id, name, sortOrder, isActive}`, and neither of these is.
           *
           *  - a CONTACT GROUP carries a `slug` with rules of its own — settable
           *    on create, refused on update — which no `MASTERS` kind has;
           *  - a BILLING REGION DEFAULT carries a slug, a currency and two Xero
           *    columns, and has NO CREATE AND NO DELETE at all. Folding it into
           *    `MASTERS` would hand it back both verbs, since every entry there
           *    gets a New button and a delete unless `deletable` says otherwise
           *    — and there is no flag for "cannot be created", because until now
           *    no list on this page needed one.
           *
           * They take `ability` and nothing else: neither has a picker to seed
           * and neither shares a fetch with anything already on this page.
           */}
          <ContactGroupsCard ability={ability} />
          <BillingRegionDefaultsCard ability={ability} />

          {/*
           * Stage statuses (owner 2026-10-23): the drill-down under each
           * company stage, grouped by stage. Self-contained because each row
           * carries a stage fixed at create, which no `MASTERS` kind has. The
           * stage list this page already loaded is passed in for the grouping
           * and the create picker rather than fetched a second time.
           */}
          <CompanyStageStatusesCard
            ability={ability}
            stages={companyStages.result?.data ?? EMPTY_STAGES}
            stagesLoading={companyStages.loading}
            stagesFailed={companyStages.error !== null}
          />
        </div>
      </section>
          ),
          /*
           * Contacts sits below the managed lists, under a heading that says what
           * it actually is. It is NOT a reference master — it is a CRM entity with
           * its own page — and filing it among the picklists would tell a reader
           * that contacts are configuration, which is the one thing this screen
           * must not say.
           */
          records: () => (
      <section aria-labelledby="catalog-records">
        <SectionHeading
          id="catalog-records"
          title="Records without a sidebar row"
        />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <EntityCard
            icon={<PeopleIcon />}
            title="Contacts"
            singular="contact"
            total={contacts.result?.meta.total ?? null}
            loading={contacts.loading}
            error={contacts.error}
            to="/contacts"
            linkLabel="Open the full contacts page"
          />
        </div>
      </section>
          ),
          /*
           * Company mapper (owner 2026-10-25, names-mapper M6): the SEED platform
           * company queue. Gated on `product-insights` read; returning null keeps
           * the block out of the grid and the layout tray alike.
           */
          'company-mapper': () =>
            ability.canRead('product-insights') ? (
      <section aria-labelledby="catalog-company-mapper">
        <SectionHeading
          id="catalog-company-mapper"
          title="Company mapper"
        />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <CardShell
            icon={<MapperIcon />}
            title="Companies to SEED platform"
            meta={null}
            action={
              <Button variant="ghost" size="sm" onClick={() => setMapperOpen(true)}>
                Open Company mapper
              </Button>
            }
          />
        </div>
      </section>
            ) : null,
          /*
           * Account lead history import (owner 2026-10-25): gated on
           * `account-lead-history` update (Management; super admin bypasses).
           */
          'account-lead-import': () =>
            canImportLeadHistory ? (
      <section aria-labelledby="catalog-account-lead-import">
        <SectionHeading
          id="catalog-account-lead-import"
          title="Account lead history"
        />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <CardShell
            icon={<LeadHistoryIcon />}
            title="Account lead history"
            meta={null}
            action={
              <Button variant="ghost" size="sm" onClick={() => setLeadImportOpen(true)}>
                Import account lead history
              </Button>
            }
          />
        </div>
      </section>
            ) : null,
        }}
      />

      {ability.canRead('product-insights') && (
        <Modal open={mapperOpen} onClose={closeMapper} title="Company mapper" size="xl">
          <CompanyMapperPanel />
        </Modal>
      )}

      {canImportLeadHistory && (
        <AccountLeadImportModal open={leadImportOpen} onClose={closeLeadImport} />
      )}

      {openMeta && openEntry && (
        <MasterListModal
          meta={openMeta}
          open
          onClose={() => setOpenKind(null)}
          rows={openEntry.rows}
          loading={openEntry.state.loading}
          error={openEntry.state.error}
          ability={ability}
          onReload={openEntry.state.reload}
        />
      )}

      {openPeriodMeta && (
        <PeriodListModal
          meta={openPeriodMeta}
          open
          onClose={() => setOpenPeriod(null)}
          rows={periodRows[openPeriodMeta.kind]}
          loading={openPeriodState.loading}
          error={openPeriodState.error}
          ability={ability}
          onReload={openPeriodState.reload}
          cycles={cycleList}
          cyclesLoading={cycles.loading}
          cyclesFailed={cycles.error !== null}
        />
      )}
    </>
  );
}
